/* eslint-disable react-refresh/only-export-components */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ArrowsClockwise, Bell, DotsThree, Envelope, Funnel, Info, MagnifyingGlass, Plus, RocketLaunch } from "@phosphor-icons/react";
import {
  Accordion,
  Alert,
  Avatar,
  Badge,
  Box,
  Breadcrumbs,
  BusinessInfoDisplayCard,
  BusinessInfoDisplayCardSkeleton,
  Button,
  ChatBubble,
  ChatComposer,
  Checkbox,
  Chip,
  Collapsible,
  ColorPicker,
  ColorSwatchGroup,
  type ColorSwatchValue,
  Container,
  Drawer,
  Dropdown,
  EmptyState,
  EntityDisplayCard,
  DateRangePicker,
  FileDropzone,
  FormField,
  FullPageLoader,
  Grid,
  HorizontalStepper,
  Icon,
  IconButton,
  InfoCard,
  Input,
  KpiCard,
  Label,
  LayoutShell,
  ListPageLayout,
  Modal,
  MultiSelect,
  NumberStepper,
  OptionCardGroup,
  PageHeader,
  Paper,
  Popover,
  ProgressBar,
  Radio,
  RevealAndCopy,
  RevealField,
  SearchableSelect,
  SelectableChipGroup,
  Select,
  Skeleton,
  Slider,
  Spinner,
  Stack,
  StepProgressCard,
  StepProgressCardSkeleton,
  SuggestInput,
  SummaryBar,
  Switch,
  TagsInput,
  DataTable,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableBulkActions,
  TableColumnVisibility,
  TablePagination,
  TableSaveView,
  TableViewSelector,
  TableToolbar,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  ToastProvider,
  Tip,
  WorkspaceHeader,
  createLocalSaveView,
  createLocalViewStore,
  createAgenticThemeVariables,
  type DataTableColumn,
  type DataTableSorting,
  type DateRangeValue,
  type SavedView,
  type ViewFilter,
  presetToRange,
  useToast
} from "@/index";
import { ComponentExample, type ComponentExampleProps } from "./ComponentExample";

export type DocsPage = {
  id: string;
  title: string;
  category: string;
  description?: string;
  content: ReactNode;
};

export type DocsFramework = "react" | "angular";

const PUBLIC_DOCS_URL = "https://agentic-components.vercel.app/";

type Customer = {
  id: number;
  name: string;
  email: string;
  status: string;
  orders: number;
  revenue: string;
  city: string;
  phone: string;
  channel: string;
  paymentMode: string;
  lastOrder: string;
  aov: string;
  returns: number;
  region: string;
  gstin: string;
};

const businessSections = [
  {
    items: [
      { label: "Business Type", value: "Registered" },
      { label: "Company Name", value: "Deedoodle" },
      { label: "Pickup Pincode", value: "122099" },
      {
        label: "Sales Channel",
        value: (
          <span className="bfrs-grid bfrs-gap-1">
            <span>Website/Online Store</span>
            <span>E-Commerce platforms</span>
            <span>Social Media</span>
          </span>
        )
      },
      { label: "Brand Name", value: "Dee-doodles" },
      { label: "Website", value: "www. deedoodle.com" }
    ]
  }
];

const progressiveBusinessSections = [
  {
    title: "Account Details",
    items: [
      { label: "Phone", value: "9876543210" },
      { label: "GST Number", value: undefined },
      { label: "Pickup Pincode", value: undefined }
    ]
  }
];

const progressSteps = [
  { label: "Securely Fetching to your account" },
  { label: "Verifying your identity" },
  { label: "Retrieving business details" },
  { label: "Validating your information" }
];

const remittanceSteps = [
  { label: "Select Plan", secondaryText: "Choose payout speed" },
  { label: "Verify OTP", secondaryText: "Confirm activation" }
];

function DemoRevealAndCopy() {
  return (
    <div className="bfrs-grid bfrs-gap-4 md:bfrs-grid-cols-3">
      <Paper className="bfrs-p-4">
        <Text variant="label" tone="muted">Masked phone</Text>
        <div className="bfrs-mt-3">
          <RevealAndCopy value="9876543210" allowCopy />
        </div>
      </Paper>
      <Paper className="bfrs-p-4">
        <Text variant="label" tone="muted">Reveal only</Text>
        <div className="bfrs-mt-3">
          <RevealAndCopy value="ops@deedoodle.com" allowCopy={false} />
        </div>
      </Paper>
      <Paper className="bfrs-p-4">
        <Text variant="label" tone="muted">Missing value</Text>
        <div className="bfrs-mt-3">
          <RevealAndCopy value="" />
        </div>
      </Paper>
    </div>
  );
}

const tableRows: Customer[] = [
  { id: 1, name: "Deeksha Gahlot", email: "deeksha@example.com", status: "Verified", orders: 34, revenue: "₹84,200", city: "Gurugram", phone: "+91 98100 11234", channel: "Shopify_1", paymentMode: "Prepaid", lastOrder: "22 Jun 2026", aov: "₹2,476", returns: 2, region: "North", gstin: "06AABCU9603R1ZP" },
  { id: 2, name: "Nykaa Fashion", email: "ops@nykaa.com", status: "Pending", orders: 18, revenue: "₹42,900", city: "Mumbai", phone: "+91 98200 55678", channel: "WooCommerce", paymentMode: "COD", lastOrder: "21 Jun 2026", aov: "₹2,383", returns: 1, region: "West", gstin: "27AAACN1234R1Z5" },
  { id: 3, name: "Northstar Retail", email: "ops@northstar.example", status: "Verified", orders: 52, revenue: "₹1,24,100", city: "Bengaluru", phone: "+91 99000 33456", channel: "Magento", paymentMode: "Prepaid", lastOrder: "20 Jun 2026", aov: "₹2,386", returns: 4, region: "South", gstin: "29AAFCN5678R1ZK" },
  { id: 4, name: "Sreyas Traders", email: "hello@sreyas.example", status: "Verified", orders: 27, revenue: "₹61,540", city: "Hyderabad", phone: "+91 90400 77890", channel: "Shopify_1", paymentMode: "COD", lastOrder: "19 Jun 2026", aov: "₹2,279", returns: 0, region: "South", gstin: "36AADCS9012R1Z9" },
  { id: 5, name: "Kalbog Exports", email: "sales@kalbog.example", status: "Pending", orders: 9, revenue: "₹18,300", city: "Chandigarh", phone: "+91 87000 22345", channel: "Amazon", paymentMode: "Prepaid", lastOrder: "18 Jun 2026", aov: "₹2,033", returns: 3, region: "North", gstin: "04AAGCK3456R1ZB" }
];

const tableColumns: DataTableColumn<Customer>[] = [
  { id: "orders", header: "Orders", accessorKey: "orders", sortable: true, width: 132 },
  { id: "status", header: "Status", width: 156, cell: (row) => <Badge tone={row.status === "Verified" ? "success" : "warning"}>{row.status}</Badge> },
  { id: "revenue", header: "Revenue", accessorKey: "revenue", sortable: true, width: 156, align: "right" },
  { id: "channel", header: "Channel", accessorKey: "channel", sortable: true, width: 168 },
  { id: "paymentMode", header: "Payment", accessorKey: "paymentMode", sortable: true, width: 140 },
  { id: "phone", header: "Phone", accessorKey: "phone", width: 188 },
  { id: "lastOrder", header: "Last order", accessorKey: "lastOrder", sortable: true, width: 160 },
  { id: "aov", header: "Avg. order value", accessorKey: "aov", sortable: true, width: 176, align: "right" },
  { id: "returns", header: "Returns", accessorKey: "returns", sortable: true, width: 132, align: "right" },
  { id: "region", header: "Region", accessorKey: "region", sortable: true, width: 140 },
  { id: "city", header: "City", accessorKey: "city", sortable: true, width: 180 },
  { id: "gstin", header: "GSTIN", accessorKey: "gstin", width: 200 }
];

const allColumnIds = tableColumns.map((column) => column.id);
const requiredColumnIds = ["orders", "status"]; // columns that cannot be hidden

const visibleIdsFromSavedView = (view: SavedView) =>
  view.columns
    .slice()
    .sort((a, b) => a.order - b.order)
    .filter((column) => column.visible)
    .map((column) => column.key)
    .filter((id) => allColumnIds.includes(id));

function DemoDataTable() {
  const [sorting, setSorting] = useState<DataTableSorting | null>({ columnId: "orders", direction: "desc" });
  const [savedVisibleColumnIds, setSavedVisibleColumnIds] = useState(allColumnIds);
  const [visibleColumnIds, setVisibleColumnIds] = useState(allColumnIds);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [resetKey, setResetKey] = useState(0);
  const visibilityColumns = useMemo(
    () => tableColumns.map((column) => ({ id: column.id, label: column.header, disabled: requiredColumnIds.includes(column.id) })),
    []
  );
  const isDirty =
    visibleColumnIds.length !== savedVisibleColumnIds.length ||
    visibleColumnIds.some((id) => !savedVisibleColumnIds.includes(id));

  const resetColumns = () => {
    setVisibleColumnIds(savedVisibleColumnIds);
    setResetKey((key) => key + 1);
  };

  return (
    <div>
      <TableToolbar
        title="Customer table"
        totalLabel={`${tableRows.length} rows`}
        className="bfrs-rounded-t-md bfrs-border bfrs-border-border bfrs-border-b-0"
        actions={
          <div className="bfrs-flex bfrs-items-center bfrs-gap-2">
            {isDirty && (
              <>
                <Button variant="secondary" size="sm" iconOnly aria-label="Discard changes" onClick={resetColumns}>
                  <ArrowsClockwise size={16} />
                </Button>
                <TableSaveView
                  columns={visibilityColumns}
                  visibleColumnIds={visibleColumnIds}
                  onSave={(payload) => setSavedVisibleColumnIds(payload.columns.filter((c) => c.visible).map((c) => c.key))}
                />
              </>
            )}
            <TableColumnVisibility
              columns={visibilityColumns}
              visibleColumnIds={visibleColumnIds}
              onVisibleColumnIdsChange={setVisibleColumnIds}
              onReset={resetColumns}
            />
          </div>
        }
      />
      <DataTable
        columns={tableColumns}
        data={tableRows}
        getRowId={(row) => String(row.id)}
        sorting={sorting}
        onSortingChange={setSorting}
        visibleColumnIds={visibleColumnIds}
        enableColumnPinning
        enableColumnReordering
        resetKey={resetKey}
        leadingColumn={{
          header: "Customer",
          width: 280,
          cell: (row) => (
            <div className="bfrs-flex bfrs-items-center bfrs-gap-3">
              <Avatar name={row.name} size="sm" status={row.status === "Verified" ? "online" : "busy"} />
              <div className="bfrs-min-w-0">
                <div className="bfrs-truncate bfrs-font-semibold bfrs-text-text-primary">{row.name}</div>
                <div className="bfrs-truncate bfrs-text-xs bfrs-text-text-muted">{row.email}</div>
              </div>
            </div>
          )
        }}
        selection
        selectedRowIds={selectedRowIds}
        onSelectedRowIdsChange={setSelectedRowIds}
        rowActions={() => <IconButton aria-label="Row actions" icon={<DotsThree size={13} />} variant="secondary" size="sm" />}
        className="bfrs-rounded-t-none"
        minWidth={1600}
      />
    </div>
  );
}

function DemoSavedViewsSelector() {
  const screenName = "docs.saved-view-selector";
  const [selectedViewName, setSelectedViewName] = useState("wd");
  const [status, setStatus] = useState("all");
  const [visibleColumnIds, setVisibleColumnIds] = useState(["orders", "status", "revenue", "channel", "city"]);
  const [filters, setFilters] = useState<ViewFilter[]>([{ field: "channel", op: "equals", value: "Shopify_1" }]);
  const visibilityColumns = useMemo(
    () => tableColumns.map((column) => ({ id: column.id, label: column.header, disabled: requiredColumnIds.includes(column.id) })),
    []
  );
  const saveCurrentView = useMemo(() => createLocalSaveView(screenName), []);
  const applySavedView = (view: SavedView | null) => {
    if (!view) return;
    setSelectedViewName(view.name);
    setVisibleColumnIds(visibleIdsFromSavedView(view));
    setFilters(view.filters ?? []);
  };

  useEffect(() => {
    const store = createLocalViewStore(screenName);
    void store.list().then(async (views) => {
      if (views.length > 0) {
        applySavedView((views.find((view) => view.is_default) ?? views[0]) ?? null);
        return;
      }

      const first = await store.save({
        view_type: screenName,
        name: "wd",
        columns: tableColumns.map((column, index) => ({
          key: column.id,
          order: index,
          visible: ["orders", "status", "revenue", "channel", "city"].includes(column.id)
        })),
        filters: [{ field: "channel", op: "equals", value: "Shopify_1" }]
      });

      await store.save({
        view_type: screenName,
        name: "Pending payments",
        columns: tableColumns.map((column, index) => ({
          key: column.id,
          order: index,
          visible: ["orders", "status", "revenue", "paymentMode", "phone"].includes(column.id)
        })),
        filters: [{ field: "status", op: "equals", value: "Pending" }]
      });

      applySavedView(first.view);
    });
  }, []);

  return (
    <ListPageLayout
      className="bfrs-overflow-hidden bfrs-rounded-xl bfrs-border bfrs-border-border"
      title="Orders"
      filters={
        <>
          <TableViewSelector screenName={screenName} onValueChange={(_, view) => applySavedView(view)} />
          <Select
            size="sm"
            value={status}
            onValueChange={setStatus}
            options={[
              { value: "all", label: "All statuses" },
              { value: "verified", label: "Verified" },
              { value: "pending", label: "Pending" }
            ]}
          />
          <Select
            size="sm"
            defaultValue="last-30"
            options={[
              { value: "last-30", label: "Last 30 days" },
              { value: "last-7", label: "Last 7 days" }
            ]}
          />
          <TableSaveView
            columns={visibilityColumns}
            visibleColumnIds={visibleColumnIds}
            filters={filters}
            filtersCount={filters.length}
            viewType="orders"
            onSave={saveCurrentView}
            onSaved={({ view }) => applySavedView(view)}
            trigger={<Button size="sm">Save view</Button>}
          />
        </>
      }
      bannerBelowFilters={<Tip>Active view: {selectedViewName}</Tip>}
      table={
        <DataTable
          columns={tableColumns.slice(0, 6)}
          data={tableRows}
          visibleColumnIds={visibleColumnIds}
          enableColumnPinning={false}
          enableColumnReordering={false}
        />
      }
    />
  );
}

const standaloneColumns = tableColumns.filter((column) => ["orders", "status", "revenue", "city"].includes(column.id));

function DemoStandaloneDataTable() {
  const [sorting, setSorting] = useState<DataTableSorting | null>({ columnId: "orders", direction: "desc" });
  return (
    <DataTable
      columns={standaloneColumns}
      data={tableRows}
      getRowId={(row) => String(row.id)}
      sorting={sorting}
      onSortingChange={setSorting}
      enableColumnPinning={false}
      enableColumnReordering={false}
    />
  );
}

function DemoEmptyDataTable() {
  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <Text variant="caption" tone="muted">Default — built-in two-line message</Text>
        <DataTable columns={standaloneColumns} data={[]} enableColumnPinning={false} enableColumnReordering={false} />
      </Stack>
      <Stack gap={1}>
        <Text variant="caption" tone="muted">Custom copy — override either line</Text>
        <DataTable
          columns={standaloneColumns}
          data={[]}
          emptyTitle="No orders yet"
          emptyDescription="New orders will show up here as they come in."
          enableColumnPinning={false}
          enableColumnReordering={false}
        />
      </Stack>
    </Stack>
  );
}

const scrollTableRows: Customer[] = Array.from({ length: 3 }).flatMap((_, block) =>
  tableRows.map((row) => ({ ...row, id: row.id + block * 100 }))
);

function DemoFixedHeightDataTable() {
  const [sorting, setSorting] = useState<DataTableSorting | null>({ columnId: "orders", direction: "desc" });
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  return (
    <DataTable
      columns={standaloneColumns}
      data={scrollTableRows}
      getRowId={(row) => String(row.id)}
      sorting={sorting}
      onSortingChange={setSorting}
      selection
      selectedRowIds={selectedRowIds}
      onSelectedRowIdsChange={setSelectedRowIds}
      maxHeight={300}
      pagination={{
        page,
        totalPages: 10,
        variant: "indexed",
        pageSize,
        pageSizeOptions: [10, 15, 20, 25, 50],
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
        pageSizeControl: "menu",
        itemsPerPageLabel: "Items per page:",
        itemLabel: "Orders"
      }}
    />
  );
}

function DemoFixedHeightColumnsDataTable() {
  const [sorting, setSorting] = useState<DataTableSorting | null>({ columnId: "orders", direction: "desc" });
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [savedColumnIds, setSavedColumnIds] = useState(allColumnIds);
  const [visibleColumnIds, setVisibleColumnIds] = useState(allColumnIds);
  const visibilityColumns = useMemo(
    () => tableColumns.map((column) => ({ id: column.id, label: column.header, disabled: requiredColumnIds.includes(column.id) })),
    []
  );
  const isDirty =
    visibleColumnIds.length !== savedColumnIds.length ||
    visibleColumnIds.some((id) => !savedColumnIds.includes(id));

  return (
    <div>
      <TableToolbar
        title="Orders"
        totalLabel={`${scrollTableRows.length} orders`}
        className="bfrs-flex-nowrap bfrs-rounded-none bfrs-border-0 bfrs-bg-transparent bfrs-px-1 bfrs-pb-3"
        actions={
          <div className="bfrs-flex bfrs-items-center bfrs-gap-2">
            {isDirty && (
              <>
                <Button variant="secondary" size="sm" iconOnly aria-label="Discard changes" onClick={() => setVisibleColumnIds(savedColumnIds)}>
                  <ArrowsClockwise size={16} />
                </Button>
                <TableSaveView
                  columns={visibilityColumns}
                  visibleColumnIds={visibleColumnIds}
                  onSave={(payload) => setSavedColumnIds(payload.columns.filter((c) => c.visible).map((c) => c.key))}
                />
              </>
            )}
            <TableColumnVisibility
              columns={visibilityColumns}
              visibleColumnIds={visibleColumnIds}
              onVisibleColumnIdsChange={setVisibleColumnIds}
              onReset={() => setVisibleColumnIds(savedColumnIds)}
            />
          </div>
        }
      />
      <DataTable
        columns={tableColumns}
        data={scrollTableRows}
        getRowId={(row) => String(row.id)}
        sorting={sorting}
        onSortingChange={setSorting}
        visibleColumnIds={visibleColumnIds}
        enableColumnPinning
        leadingColumn={{
          header: "Customer",
          width: 280,
          cell: (row) => (
            <div className="bfrs-flex bfrs-items-center bfrs-gap-3">
              <Avatar name={row.name} size="sm" status={row.status === "Verified" ? "online" : "busy"} />
              <div className="bfrs-min-w-0">
                <div className="bfrs-truncate bfrs-font-semibold bfrs-text-text-primary">{row.name}</div>
                <div className="bfrs-truncate bfrs-text-xs bfrs-text-text-muted">{row.email}</div>
              </div>
            </div>
          )
        }}
        selection
        selectedRowIds={selectedRowIds}
        onSelectedRowIdsChange={setSelectedRowIds}
        rowActions={() => <IconButton aria-label="Row actions" icon={<DotsThree size={13} />} variant="secondary" size="sm" />}
        rowActionsHeader="Actions"
        rowActionsWidth={88}
        stickyRowActions
        maxHeight={300}
        minWidth={1600}
        pagination={{
          page,
          totalPages: 10,
          variant: "simple",
          pageSize,
          pageSizeOptions: [10, 15, 20, 25, 50],
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
          pageSizeControl: "menu",
          itemsPerPageLabel: "Items per page:",
          itemLabel: "Orders"
        }}
      />
    </div>
  );
}

function DemoPaginationVariants() {
  const [indexedPage, setIndexedPage] = useState(2);
  const [simplePage, setSimplePage] = useState(2);

  return (
    <Stack gap={3}>
      <TablePagination page={indexedPage} totalPages={10} variant="indexed" onPageChange={setIndexedPage} />
      <TablePagination page={simplePage} totalPages={10} variant="simple" onPageChange={setSimplePage} />
    </Stack>
  );
}

const primitiveTableRows = [
  { id: "ORD-1001", customer: "Acme Corp", amount: "₹1,24,500", status: "Delivered" },
  { id: "ORD-1002", customer: "Shiprocket Ltd", amount: "₹48,200", status: "In Transit" },
  { id: "ORD-1003", customer: "BlueDart Express", amount: "₹92,750", status: "Pending" },
  { id: "ORD-1004", customer: "Delhivery Inc", amount: "₹2,05,000", status: "Delivered" },
];

function DemoPrimitiveTable() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <Stack gap={4}>
      <Table density="default" aria-label="Orders demo">
        <Thead sticky>
          <Tr>
            <Th>Order ID</Th>
            <Th>Customer</Th>
            <Th align="right">Amount</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {primitiveTableRows.map((row) => (
            <Tr key={row.id} onClick={() => setSelected(row.id)}>
              <Td>{row.id}</Td>
              <Td>{row.customer}</Td>
              <Td align="right">{row.amount}</Td>
              <Td>{row.status}</Td>
            </Tr>
          ))}
        </Tbody>
        <Tfoot>
          <Tr>
            <Th colSpan={2}>Total</Th>
            <Th align="right">₹4,70,450</Th>
            <Th />
          </Tr>
        </Tfoot>
      </Table>
      {selected && <Text variant="body-sm">Selected: {selected}</Text>}
    </Stack>
  );
}

function DemoCompactTable() {
  return (
    <Table density="compact" maxHeight={200} aria-label="Compact orders">
      <Thead sticky>
        <Tr>
          <Th>Order ID</Th>
          <Th>Customer</Th>
          <Th align="right">Amount</Th>
        </Tr>
      </Thead>
      <Tbody>
        {primitiveTableRows.map((row) => (
          <Tr key={row.id}>
            <Td>{row.id}</Td>
            <Td>{row.customer}</Td>
            <Td align="right">{row.amount}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

const dataTableApiRows: Array<{ name: string; description: string }> = [
  { name: "KpiCard label / value", description: "Heading and metric for a single KPI. value accepts any node (string, number, or formatted markup)." },
  { name: "KpiCard accent", description: "Colors the left bar: primary (default), success, warning, danger, info, or none. Use it to differentiate metrics on a KPI page." },

  { name: "data", description: "Array of row objects. DataTable is presentational — it never fetches; pass already-loaded, formatted rows." },
  { name: "columns", description: "Array of column definitions (see column fields below). Their order here is the default column order." },
  { name: "getRowId", description: "(row, index) => string. Stable id used for selection, React keys, and hover/row identity. Falls back to row.id, then the index." },

  { name: "column.id", description: "Unique column key — referenced by column order, pinning, visibility, and sort state." },
  { name: "column.header", description: "Header content: a string or any node." },
  { name: "column.accessorKey / accessorFn", description: "How a cell value is read: a key of the row object, or (row) => value. accessorFn takes precedence when both are present." },
  { name: "column.cell", description: "(row, ctx) => node custom renderer; ctx = { value, rowIndex, selected, hovered }. Keep product-specific formatting (badges, links) here." },
  { name: "column.sortable", description: "Enables the sortable header button for this column (requires enableSorting)." },
  { name: "column.sortValue", description: "(row) => value used only for sorting — e.g. sort a formatted '₹1,24,100' string by its underlying number." },
  { name: "column.width / minWidth", description: "Column width (number px or CSS string). Declaring widths is required for predictable pinning — see the pinning note." },
  { name: "column.align", description: "left | center | right alignment applied to both the header and the cells." },
  { name: "column.className / headerClassName / style / headerStyle", description: "Per-column class and inline-style overrides for cells and the header." },

  { name: "enableSorting", description: "Defaults true. Master switch for sortable headers and data sorting." },
  { name: "sorting / onSortingChange", description: "Controlled sort state { columnId, direction: 'asc' | 'desc' }. Omit `sorting` to let the table manage it internally." },
  { name: "defaultSorting", description: "Initial sort used in uncontrolled mode." },
  { name: "showSortIcon", description: "always (default) | active | never — controls when the sort glyph is shown on sortable headers." },

  { name: "visibleColumnIds", description: "Controlled list of visible column ids. Pair with TableColumnVisibility for the Columns menu. Omit to render every column." },
  { name: "columnOrder / defaultColumnOrder / onColumnOrderChange", description: "Controlled column order by id; drag-reordering updates it. Omit to manage order internally." },
  { name: "enableColumnReordering", description: "Defaults true. Drag visible column headers to reorder them; renderers stay app-owned." },

  { name: "enableColumnPinning", description: "Defaults true. Shows Pin/Unpin header controls and freezes (sticky) the leading column + the first N data columns." },
  { name: "pinnedColumnCount / defaultPinnedColumnCount / onPinnedColumnCountChange", description: "How many leading data columns are frozen. Controlled or uncontrolled. 'Pin up to column N' sets this to N." },
  { name: "pinLeadingColumn / onPinLeadingColumnChange", description: "Whether the leadingColumn is frozen. It is also frozen automatically whenever any data column is pinned (it sits to their left)." },
  { name: "Pinning + widths (note)", description: "Sticky offsets are measured from rendered header widths, so pinned columns stay fixed even when the table stretches. Declare widths for predictable pinned layouts." },

  { name: "leadingColumn", description: "A special identity column rendered before the data columns: { header, cell, width, minWidth, className, ... }. Typically a name/avatar; it hosts the select-all checkbox and is pinned together with the data columns." },

  { name: "selection / selectedRowIds / onSelectedRowIdsChange", description: "Enables per-row checkboxes with controlled selected ids. The header checkbox toggles all currently visible rows." },

  { name: "rowActions", description: "(row) => node trailing per-row action slot — e.g. an IconButton that opens a menu. Clicks here do not trigger onRowClick." },
  { name: "rowActionsHeader / rowActionsWidth", description: "Header label and width for the actions column." },
  { name: "stickyRowActions", description: "Freeze the actions column to the right edge. Defaults true when rowActions is provided." },

  { name: "loading", description: "Renders built-in table-row skeleton placeholders (not a spinner) instead of rows." },
  { name: "error", description: "Renders the error state with this node/message in place of rows." },
  { name: "emptyState / emptyTitle / emptyDescription", description: "When data is empty: a fully custom emptyState node, or a title + description for the built-in empty state. The built-in view is a two-line message and defaults to \"No records found\" + \"There's nothing to show here yet.\"; override either line. (TableEmptyState, used directly, requires both title and description.)" },

  { name: "stickyHeader", description: "Defaults true. The header row sticks to the top of the scroll area while the body scrolls." },
  { name: "minWidth", description: "Minimum table width (default '100%'). Set a px value to force horizontal scrolling when there are many columns." },
  { name: "maxHeight", description: "Caps the scroll region height so rows scroll vertically while the sticky header stays pinned. Combine with the pagination prop for a fixed-height table with a fixed footer." },
  { name: "Figma table metrics", description: "Single default density: 38px header, 64px rows, 12px table text, and Figma-matched spacing." },
  { name: "className / tableClassName / headerClassName / rowClassName / cellClassName", description: "Class overrides for the card wrapper, the <table>, the header, rows, and cells. rowClassName can be a (row, index) function." },
  { name: "onRowClick", description: "(row) => void; makes rows clickable. Clicks on the selection checkbox and the actions cell are excluded." },
  { name: "aria-label", description: "Accessible name for the table (default 'Data table')." },
  { name: "pagination", description: "Inline footer pagination (TablePaginationProps). Rendered inside the card, below the scroll area — stays fixed when maxHeight is set." },

  { name: "TableColumnVisibility columns", description: "[{ id, label, disabled? }]. disabled marks a required column that is always visible and cannot be unchecked (e.g. the identity columns)." },
  { name: "TableColumnVisibility visibleColumnIds / onVisibleColumnIdsChange", description: "Current visible ids and the change handler. The menu stays open while toggling so several columns can be changed before saving." },
  { name: "TableColumnVisibility onReset / onClearFilters / onShowAll", description: "Footer buttons. 'Reset' reverts columns to the saved/default set (onReset) and clears active filters (onClearFilters) — supplying either renders the Reset button and both run together; 'Show all' makes every column visible (default behavior, override via onShowAll)." },
  { name: "TableColumnVisibility label / resetLabel / showAllLabel", description: "Customizable text for the menu header/trigger, the Reset button, and the Show all button." },
  { name: "TableViewSelector", description: "Saved-views trigger and management menu for the list-page filters band. Pass a free-form screenName; by default it reads/writes that screen's views from localStorage via createLocalViewStore(screenName), stays hidden until that screen has at least one saved view, selects the primary view when no value is present, and emits selection, primary, rename, delete, and switch-to-default callbacks. Pass saveApi with explicit viewType and tab so the selector itself owns backend list/get/update/delete plus the same localStorage mirror/fallback." },
  { name: "TableSaveView", description: "Opens the Save this view modal. Collects a view name, summarizes hidden columns / active filters, and builds SaveViewPayload. Pass screenName to let the component save into the local store; pass saveApi when it should POST through the component-owned backend path. Use onSaved/onError for app UI state." },
  { name: "useSavedViews", description: "React hook for a read-only list of the local saved-view mirror for a screenName. Use it when app state needs the current views without importing raw saved-view API helpers." },

  { name: "TablePagination page / totalPages / onPageChange", description: "Current page, total page count, and the page-change handler. Used by the Prev / numbered / Next controls." },
  { name: "TablePagination variant", description: "indexed (default) renders Prev, page indexes such as 1, 2, 3, ..., 10, and Next. simple renders only Prev and Next. Works standalone and inside DataTable's pagination prop." },
  { name: "TablePagination pageSize / pageSizeOptions / onPageSizeChange", description: "Page-size control. The 'Items per page' cluster appears only when both pageSize and onPageSizeChange are provided." },
  { name: "TablePagination pageSizeControl", description: "select (native dropdown) or menu (a '{pageSize} {itemLabel}' button that opens a menu of pageSizeOptions)." },
  { name: "TablePagination itemsPerPageLabel / itemLabel", description: "Customizable text: the left label (default 'Items per page:') and the unit suffix (default 'items', e.g. 'Orders' → '15 Orders')." },
  { name: "TablePagination showPageNavigation", description: "Defaults true; set false to render only the page-size cluster (e.g. for single-page tables)." }
];

function DemoDateRangePicker() {
  const [range, setRange] = useState<DateRangeValue>(() => ({
    preset: "Last 30 days",
    ...presetToRange("Last 30 days")
  }));

  return <DateRangePicker value={range} onChange={setRange} maxRangeDays={365} />;
}

function DemoDateRangePickerWithTable() {
  const [range, setRange] = useState<DateRangeValue>(() => ({
    preset: "Last 30 days",
    ...presetToRange("Last 30 days")
  }));
  const [sorting, setSorting] = useState<DataTableSorting | null>({ columnId: "orders", direction: "desc" });

  return (
    <Stack gap={3}>
      <DateRangePicker value={range} onChange={setRange} maxRangeDays={365} />
      <DataTable
        columns={tableColumns}
        data={tableRows}
        getRowId={(row) => String(row.id)}
        sorting={sorting}
        onSortingChange={setSorting}
        minWidth={1600}
      />
    </Stack>
  );
}

const businessSelectOptions = [
  { value: "registered", label: "Registered business" },
  { value: "individual", label: "Individual seller" },
  { value: "marketplace", label: "Marketplace seller" }
];

const businessRadioOptions = [
  { value: "registered", label: "Registered business", description: "Company GST and business KYC are available." },
  { value: "individual", label: "Individual seller", description: "Use personal details for seller verification." },
  { value: "marketplace", label: "Marketplace seller", description: "Sell primarily through marketplace channels." }
];

const channelSelectOptions = [
  { value: "amazon", label: "Amazon" },
  { value: "flipkart", label: "Flipkart" },
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "magento", label: "Magento" },
  { value: "custom", label: "Custom storefront", disabled: true }
];

// Options carrying a `group` render under grouped headings.
const groupedSelectOptions = [
  { value: "cod", label: "Cash on Delivery", group: "Payment" },
  { value: "prepaid", label: "Prepaid", group: "Payment" },
  { value: "wallet", label: "Wallet", group: "Payment" },
  { value: "dom", label: "Domestic", group: "Shipping" },
  { value: "intl", label: "International", group: "Shipping" }
];

const tagSuggestions = ["urgent", "fragile", "cod", "vip", "return-risk", "gift-wrap", "priority-pickup"];

// Deferred-commit MultiSelect: onValueChange fires only on Apply, never per-toggle.
function DemoMultiSelectApply() {
  const [applied, setApplied] = useState<string[]>(["amazon", "shopify"]);
  return (
    <Stack gap={2}>
      <MultiSelect
        options={channelSelectOptions}
        commitMode="apply"
        value={applied}
        onValueChange={setApplied}
        placeholder="Filter channels"
      />
      <Text variant="caption" tone="muted">Committed only on Apply: {applied.join(", ") || "none"}</Text>
    </Stack>
  );
}

function DemoTagInput() {
  const [tags, setTags] = useState<string[]>(["urgent", "fragile"]);
  return <TagsInput value={tags} onChange={setTags} suggestions={tagSuggestions} creatable placeholder="Add Tag" />;
}

const colorSwatchOptions: ColorSwatchValue[] = [
  "#5B45C2",
  "#2563EB",
  "#059669",
  ["#5B45C2", "#2563EB", "#059669"],
  ["#29343D", "#F1ECFB", "#FFFFFF"]
];

function DemoRangeAndColorControls() {
  const [confidence, setConfidence] = useState(72);
  const [brandColor, setBrandColor] = useState("#5B45C2");
  const [palette, setPalette] = useState<ColorSwatchValue>(colorSwatchOptions[3]);

  return (
    <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
      <Slider
        label="Confidence threshold"
        value={confidence}
        min={0}
        max={100}
        step={5}
        helperText="Use for bounded numeric settings."
        formatValue={(value) => `${value}%`}
        onValueChange={setConfidence}
      />
      <ColorPicker label="Brand color" value={brandColor} helperText="Accepts six-digit hex values." onValueChange={setBrandColor} />
      <div className="md:bfrs-col-span-2">
        <ColorSwatchGroup
          label="Palette"
          value={palette}
          options={colorSwatchOptions}
          getOptionLabel={(option, index) => (Array.isArray(option) ? `Palette ${index + 1}` : `Color ${option}`)}
          onValueChange={setPalette}
        />
      </div>
    </div>
  );
}

function DemoAdditionalFormControls() {
  const [fulfillmentMode, setFulfillmentMode] = useState("standard");
  const [channels, setChannels] = useState<string | string[]>(["whatsapp"]);
  const [quantity, setQuantity] = useState(2);
  const [city, setCity] = useState("Gurugram");
  const [orderTags, setOrderTags] = useState<string[]>(["urgent", "fragile"]);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);

  return (
    <Stack gap={5}>
      <OptionCardGroup
        value={fulfillmentMode}
        onValueChange={setFulfillmentMode}
        options={[
          { value: "standard", label: "Standard", description: "Lowest cost option for regular orders.", helpText: "Best when delivery speed is flexible." },
          { value: "express", label: "Express", description: "Prioritize faster allocation and pickup.", helpText: "Use for time-sensitive orders." }
        ]}
      />
      <SelectableChipGroup
        multiple
        value={channels}
        onValueChange={setChannels}
        addAction={{ label: "Add" }}
        options={[
          { value: "whatsapp", label: "WhatsApp" },
          { value: "instagram", label: "Instagram" },
          { value: "facebook", label: "Facebook" }
        ]}
      />
      <div className="bfrs-grid bfrs-gap-4 md:bfrs-grid-cols-2">
        <FormField label="Quantity">
          <NumberStepper value={quantity} min={1} max={10} onValueChange={setQuantity} />
        </FormField>
        <FormField label="City">
          <SuggestInput
            value={city}
            onValueChange={setCity}
            suggestions={[
              { value: "Gurugram", label: "Gurugram", description: "Haryana" },
              { value: "Mumbai", label: "Mumbai", description: "Maharashtra" },
              { value: "Bengaluru", label: "Bengaluru", description: "Karnataka" }
            ]}
          />
        </FormField>
        <FormField label="Order tags">
          <TagsInput value={orderTags} onChange={setOrderTags} suggestions={tagSuggestions} creatable placeholder="Add Tag" />
        </FormField>
        <div className="md:bfrs-col-span-2">
          <FileDropzone title="Upload invoice" description="PDF, JPG, or PNG up to the app-owned limit." files={invoiceFiles} onFilesChange={setInvoiceFiles} />
        </div>
        <RevealField label="Masked account field" value="9876543210" allowCopy helperText="Use for field-level sensitive values." />
      </div>
    </Stack>
  );
}

function ToastDocsActions() {
  const { success, info, danger } = useToast();

  return (
    <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-2">
      <Button size="sm" onClick={() => success("Action completed")}>Success toast</Button>
      <Button size="sm" variant="outline" onClick={() => info({ title: "Report queued", description: "You can keep working while it runs." })}>Info toast</Button>
      <Button size="sm" variant="destructive" onClick={() => danger({ title: "Action failed", description: "Try again in a moment." })}>Danger toast</Button>
    </div>
  );
}

function DemoToastManager() {
  return (
    <ToastProvider>
      <ToastDocsActions />
    </ToastProvider>
  );
}

function DemoCalloutPatterns() {
  return (
    <Stack gap={3}>
      <InfoCard title="Courier allocation is ready" description="Review serviceability before assigning the shipment." tone="info" />
      <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-2">
        <Tip>Recommended</Tip>
        <Tip tone="success">Verified</Tip>
        <Tip tone="warning">Needs review</Tip>
      </div>
    </Stack>
  );
}

function DemoListPageLayout() {
  const [tab, setTab] = useState("new");
  const [status, setStatus] = useState("all");
  return (
    <ListPageLayout
      className="bfrs-overflow-hidden bfrs-rounded-xl bfrs-border bfrs-border-border"
      title="Orders"
      actions={
        <>
          <Button size="sm" leftIcon={<Icon icon={Plus} />}>Add Order</Button>
          <Button size="sm" variant="outline" leftIcon={<Icon icon={ArrowsClockwise} />}>Sync Orders</Button>
        </>
      }
      kpis={
        <>
          <KpiCard label="Total orders" value="140" />
          <KpiCard label="Delivered" value="128" accent="success" />
          <KpiCard label="NDR pending" value="12" accent="warning" />
          <KpiCard label="RTO" value="03" accent="danger" />
        </>
      }
      bannerAboveTabs={<Alert tone="warning" title="KYC pending">Upload your GST certificate by Friday to avoid COD holds.</Alert>}
      banner={<Alert tone="info" title="Diwali surge">Carrier pickups may be delayed in metro hubs this week.</Alert>}
      bannerBelowFilters={<Tip>Showing orders from the last 30 days. Clear filters to see all orders.</Tip>}
      tabs={
        <Tabs
          value={tab}
          onValueChange={setTab}
          items={[
            { value: "new", label: "New Orders" },
            { value: "ready", label: "Ready to Ship" },
            { value: "manifested", label: "Manifested" }
          ]}
        />
      }
      filters={
        <Select
          size="sm"
          value={status}
          onValueChange={setStatus}
          options={[
            { value: "all", label: "All statuses" },
            { value: "verified", label: "Verified" },
            { value: "pending", label: "Pending" }
          ]}
        />
      }
      table={<DataTable data={tableRows} columns={tableColumns.slice(0, 6)} selection aria-label="Orders list" />}
    />
  );
}

function DemoWorkspaceHeader() {
  return (
    <div className="bfrs-overflow-x-auto bfrs-rounded-xl bfrs-border bfrs-border-border">
      <WorkspaceHeader
        brand={
          <div className="bfrs-flex bfrs-items-center bfrs-gap-2 bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">
            <RocketLaunch className="bfrs-text-primary" weight="fill" /> Shiprocket
          </div>
        }
        onToggleSidebar={() => undefined}
        tabs={<Text variant="body-sm" tone="muted">Orders workspace</Text>}
        search={<Input size="sm" placeholder="Search orders, AWB, SKU…" leftIcon={<Icon icon={MagnifyingGlass} />} />}
        actions={
          <Stack direction="row" gap={2} align="center">
            <IconButton variant="ghost" size="sm" aria-label="Notifications" icon={<Icon icon={Bell} />} />
            <Avatar name="Deeksha Gahlot" size="sm" />
          </Stack>
        }
      />
    </div>
  );
}

function DemoDisplayPatterns() {
  return (
    <Stack gap={4}>
      <EntityDisplayCard
        title="Dee-doodles warehouse"
        description="Plot 42, Sector 18, Gurugram, Haryana"
        status="Verified"
        metadata={[
          { label: "Pincode", value: "122099" },
          { label: "Contact", value: "9876543210" }
        ]}
        actions={[{ id: "edit", label: "Edit" }]}
        onAction={() => undefined}
      />
      <SummaryBar
        items={[
          { label: "Subtotal", value: "₹840" },
          { label: "Charges", value: "₹60" }
        ]}
        totalLabel="Total"
        totalValue="₹900"
      />
      <Collapsible title="Courier rules" description="Expandable inline section">
        <Text variant="body-sm" tone="muted">Rules and restrictions stay visible only when expanded.</Text>
      </Collapsible>
      <Accordion
        defaultValue="pickup"
        items={[
          { value: "pickup", title: "Pickup details", description: "Warehouse and contact", content: <Text variant="body-sm" tone="muted">Plot 42, Sector 18, Gurugram. Contact 9876543210.</Text> },
          { value: "shipping", title: "Shipping preferences", content: <Text variant="body-sm" tone="muted">Standard delivery with surface priority.</Text> },
          { value: "billing", title: "Billing", content: <Text variant="body-sm" tone="muted">Prepaid wallet linked to the account.</Text> }
        ]}
      />
    </Stack>
  );
}

type ValidatedFormValues = {
  businessName: string;
  email: string;
  pickupPincode: string;
};

type ValidatedFormErrors = Partial<Record<keyof ValidatedFormValues, string>>;

const initialValidatedFormValues: ValidatedFormValues = {
  businessName: "Dee-doodles",
  email: "ops@deedoodles.com",
  pickupPincode: "000000"
};

function validateShipmentForm(values: ValidatedFormValues): ValidatedFormErrors {
  const errors: ValidatedFormErrors = {};

  if (!values.businessName.trim()) {
    errors.businessName = "Business name is required.";
  }

  if (!values.email.trim()) {
    errors.email = "Email is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = "Enter a valid email address.";
  }

  if (!/^\d{6}$/.test(values.pickupPincode)) {
    errors.pickupPincode = "Enter a 6-digit pickup pincode.";
  }

  return errors;
}

async function validatePickupPincode(pincode: string) {
  await new Promise((resolve) => window.setTimeout(resolve, 550));
  return pincode === "000000" ? "Pickup is not serviceable for this pincode." : undefined;
}

function DemoValidatedFormContent() {
  const { success, danger } = useToast();
  const [values, setValues] = useState<ValidatedFormValues>(initialValidatedFormValues);
  const [errors, setErrors] = useState<ValidatedFormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field: keyof ValidatedFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const resetForm = () => {
    setValues(initialValidatedFormValues);
    setErrors({});
  };

  const submitForm = async (event: FormEvent) => {
    event.preventDefault();

    const clientErrors = validateShipmentForm(values);
    if (Object.values(clientErrors).some(Boolean)) {
      setErrors(clientErrors);
      danger({ title: "Validation failed", description: "Fix the highlighted fields before submitting." });
      return;
    }

    setSubmitting(true);
    const pincodeError = await validatePickupPincode(values.pickupPincode);
    setSubmitting(false);

    if (pincodeError) {
      setErrors({ pickupPincode: pincodeError });
      danger({ title: "API validation failed", description: "The serviceability check returned a field error." });
      return;
    }

    setErrors({});
    success({ title: "Form validated", description: "The shipment form is ready to submit." });
  };

  return (
    <form onSubmit={submitForm} className="bfrs-grid bfrs-max-w-2xl bfrs-gap-4">
      <div className="bfrs-grid bfrs-gap-4 md:bfrs-grid-cols-2">
        <FormField label="Business name" required errorText={errors.businessName}>
          <Input
            error={Boolean(errors.businessName)}
            placeholder="Dee-doodles"
            value={values.businessName}
            onChange={(event) => updateField("businessName", event.currentTarget.value)}
          />
        </FormField>
        <FormField label="Email" required errorText={errors.email} helperText="Used for shipment notifications.">
          <Input
            error={Boolean(errors.email)}
            leftIcon={<Envelope size={18} />}
            placeholder="ops@example.com"
            type="email"
            value={values.email}
            onChange={(event) => updateField("email", event.currentTarget.value)}
          />
        </FormField>
        <FormField label="Pickup pincode" required errorText={errors.pickupPincode} helperText="Serviceability is checked by the API on submit.">
          <Input
            error={Boolean(errors.pickupPincode)}
            inputMode="numeric"
            maxLength={6}
            placeholder="122099"
            suffix="IN"
            value={values.pickupPincode}
            onChange={(event) => updateField("pickupPincode", event.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
          />
        </FormField>
      </div>
      <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-2">
        <Button type="submit" loading={submitting}>Validate form</Button>
        <Button type="button" variant="outline" disabled={submitting} onClick={resetForm}>Reset</Button>
      </div>
    </form>
  );
}

function DemoValidatedForm() {
  return (
    <ToastProvider>
      <DemoValidatedFormContent />
    </ToastProvider>
  );
}

const themePreviewVariables = createAgenticThemeVariables({
  colors: {
    primary: "#5B45C2",
    primaryHover: "#4C35AE",
    primarySoft: "#F1ECFB",
    primaryBorder: "#D8CEF9",
    borderFocus: "#5B45C2"
  },
  radius: {
    control: "14px",
    card: "18px"
  },
  shadows: {
    card: "0 12px 32px rgb(91 69 194 / 0.16)",
    elevated: "0 18px 48px rgb(91 69 194 / 0.22)"
  }
});

function DemoThemeOverrides() {
  return (
    <div style={themePreviewVariables} className="bfrs-max-w-xl">
      <Paper>
        <Stack gap={4}>
          <div>
            <Text variant="title">Theme preview</Text>
            <Text variant="body-sm" tone="muted">These controls inherit scoped color, radius, and shadow variables.</Text>
          </div>
          <div className="bfrs-grid bfrs-gap-3 md:bfrs-grid-cols-[1fr_auto]">
            <Input placeholder="Pickup pincode" />
            <Button>Continue</Button>
          </div>
          <div className="bfrs-grid bfrs-grid-cols-3 bfrs-gap-2">
            <div className="bfrs-h-10 bfrs-rounded-[var(--bfrs-radius-control)] bfrs-bg-primary" />
            <div className="bfrs-h-10 bfrs-rounded-[var(--bfrs-radius-control)] bfrs-bg-primary-soft" />
            <div className="bfrs-h-10 bfrs-rounded-[var(--bfrs-radius-control)] bfrs-border bfrs-border-primary-border bfrs-bg-surface-card bfrs-shadow-card" />
          </div>
        </Stack>
      </Paper>
    </div>
  );
}

function DemoKpiCard() {
  return (
    <div className="bfrs-grid bfrs-gap-4 sm:bfrs-grid-cols-2 lg:bfrs-grid-cols-4">
      <KpiCard label="Total orders" value="05" />
      <KpiCard label="Delivered" value="128" accent="success" />
      <KpiCard label="NDR pending" value="12" accent="warning" />
      <KpiCard label="RTO" value="03" accent="danger" />
    </div>
  );
}

function DemoProgressBar() {
  return (
    <Stack gap={4}>
      <ProgressBar value={64} label="Upload progress" showValue />
      <div className="bfrs-grid bfrs-gap-3 md:bfrs-grid-cols-2">
        <ProgressBar value={30} tone="info" size="sm" />
        <ProgressBar value={56} tone="warning" size="md" />
        <ProgressBar value={82} tone="success" size="lg" />
        <ProgressBar value={94} tone="danger" />
      </div>
      <ProgressBar indeterminate label="Syncing changes…" />
    </Stack>
  );
}

type Swatch = { name: string; value: string; caption?: string };

function ColorSwatch({ name, value, caption }: Swatch) {
  return (
    <div className="bfrs-flex bfrs-flex-col bfrs-gap-2">
      <div className="bfrs-h-16 bfrs-w-full bfrs-rounded-lg bfrs-border bfrs-border-border" style={{ background: value }} />
      <div className="bfrs-grid bfrs-gap-0.5">
        <Text variant="body-sm" className="bfrs-font-semibold">{name}</Text>
        {caption ? <Text variant="caption" tone="muted">{caption}</Text> : null}
      </div>
    </div>
  );
}

function SwatchGroup({ title, description, swatches }: { title: string; description: string; swatches: Swatch[] }) {
  return (
    <Stack gap={3}>
      <div>
        <Text variant="title">{title}</Text>
        <Text variant="body-sm" tone="muted">{description}</Text>
      </div>
      <div className="bfrs-grid bfrs-grid-cols-2 bfrs-gap-4 sm:bfrs-grid-cols-3 md:bfrs-grid-cols-6">
        {swatches.map((swatch) => (
          <ColorSwatch key={swatch.name} {...swatch} />
        ))}
      </div>
    </Stack>
  );
}

const neutralRamp: Swatch[] = [
  { name: "Ink", value: "#29343D", caption: "#29343D · text & primary" },
  { name: "Muted", value: "#64737E", caption: "#64737E · secondary text" },
  { name: "Sage", value: "#D3DFD7", caption: "#D3DFD7 · strong border" },
  { name: "Mist", value: "#E5EBEA", caption: "#E5EBEA · border & muted" },
  { name: "Off-white", value: "#F6F9F6", caption: "#F6F9F6 · app background" },
  { name: "White", value: "#FFFFFF", caption: "#FFFFFF · card surface" }
];

const surfaceTokens: Swatch[] = [
  { name: "surface-app", value: "hsl(var(--bfrs-color-surface-app))", caption: "bg-surface-app" },
  { name: "surface-card", value: "hsl(var(--bfrs-color-surface-card))", caption: "bg-surface-card" },
  { name: "surface-muted", value: "hsl(var(--bfrs-color-surface-muted))", caption: "bg-surface-muted" },
  { name: "surface-raised", value: "hsl(var(--bfrs-color-surface-raised))", caption: "bg-surface-raised" }
];

const textTokens: Swatch[] = [
  { name: "text-primary", value: "hsl(var(--bfrs-color-text-primary))", caption: "text-text-primary" },
  { name: "text-secondary", value: "hsl(var(--bfrs-color-text-secondary))", caption: "text-text-secondary" },
  { name: "text-muted", value: "hsl(var(--bfrs-color-text-muted))", caption: "text-text-muted" },
  { name: "text-disabled", value: "hsl(var(--bfrs-color-text-disabled))", caption: "text-text-disabled" }
];

const borderTokens: Swatch[] = [
  { name: "border-default", value: "hsl(var(--bfrs-color-border-default))", caption: "border-border" },
  { name: "border-light", value: "hsl(var(--bfrs-color-border-light))", caption: "border-border-light" },
  { name: "border-strong", value: "hsl(var(--bfrs-color-border-strong))", caption: "border-border-strong" },
  { name: "border-focus", value: "hsl(var(--bfrs-color-border-focus))", caption: "border-border-focus" }
];

const semanticTokens: Swatch[] = [
  { name: "primary", value: "hsl(var(--bfrs-color-primary))", caption: "bg-primary" },
  { name: "accent", value: "hsl(var(--bfrs-color-accent))", caption: "bg-accent" },
  { name: "success", value: "hsl(var(--bfrs-color-success))", caption: "bg-success" },
  { name: "danger", value: "hsl(var(--bfrs-color-danger))", caption: "bg-danger" },
  { name: "warning", value: "hsl(var(--bfrs-color-warning))", caption: "bg-warning" },
  { name: "info", value: "hsl(var(--bfrs-color-info))", caption: "bg-info" }
];

function DemoColorPalette() {
  return (
    <Stack gap={6}>
      <SwatchGroup
        title="Neutral ramp"
        description="Six-step grayscale that backs every surface, border, and text token."
        swatches={neutralRamp}
      />
      <SwatchGroup title="Surfaces" description="Layered backgrounds from app shell to raised surfaces." swatches={surfaceTokens} />
      <SwatchGroup title="Text" description="Foreground tokens for primary, secondary, muted, and disabled copy." swatches={textTokens} />
      <SwatchGroup title="Borders" description="Divider and focus-ring tokens." swatches={borderTokens} />
      <SwatchGroup title="Semantic" description="Brand and status colors. Each status also exposes -soft and -contrast variants." swatches={semanticTokens} />
    </Stack>
  );
}

function Example(props: ComponentExampleProps) {
  return <ComponentExample {...props} />;
}

function ComponentPage({ examples }: { examples: ComponentExampleProps[] }) {
  return (
    <Stack gap={5}>
      {examples.map((example) => (
        <Example key={example.title} {...example} />
      ))}
    </Stack>
  );
}

function ApiNotes({ rows }: { rows: Array<{ name: string; description: string }> }) {
  return (
    <Paper padding="none" className="bfrs-overflow-hidden">
      <div className="bfrs-border-b bfrs-border-border bfrs-px-5 bfrs-py-4">
        <Text variant="title">API notes</Text>
        <Text variant="body-sm" tone="muted" className="bfrs-mt-1">Common interaction and customization props.</Text>
      </div>
      <div className="bfrs-overflow-x-auto">
        <table className="bfrs-min-w-full bfrs-divide-y bfrs-divide-border bfrs-text-left">
          <thead className="bfrs-bg-surface-muted">
            <tr>
              <th className="bfrs-px-4 bfrs-py-3 bfrs-text-xs bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.12em] bfrs-text-text-muted">Prop</th>
              <th className="bfrs-px-4 bfrs-py-3 bfrs-text-xs bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.12em] bfrs-text-text-muted">Description</th>
            </tr>
          </thead>
          <tbody className="bfrs-divide-y bfrs-divide-border bfrs-bg-surface-card">
            {rows.map((row) => (
              <tr key={row.name}>
                <td className="bfrs-whitespace-nowrap bfrs-px-4 bfrs-py-3 bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{row.name}</td>
                <td className="bfrs-px-4 bfrs-py-3 bfrs-text-sm bfrs-leading-6 bfrs-text-text-muted">{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Paper>
  );
}

function createAngularInstallationPage(): DocsPage {
  return {
    id: "installation",
    title: "Installation",
    category: "Start",
    description: "Install the public package from npmjs and register the custom elements bridge.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Install for Angular",
              description: "The same public npm package includes the custom elements bundle used by Angular apps.",
              code: `npm install @bfrs/agentic-components`,
              children: (
                <Stack gap={3}>
                  <Text variant="title">Package</Text>
                  <Text variant="body-sm" tone="muted">Install `@bfrs/agentic-components` directly from the public npm registry.</Text>
                  <Text variant="body-sm" tone="muted">Register the package custom elements once before Angular bootstraps your application.</Text>
                </Stack>
              )
            },
            {
              title: "Register custom elements",
              description: "Load the web component definitions once, import styles globally, and allow custom tags in Angular templates.",
              code: `// main.ts
import "@bfrs/agentic-components/custom-elements";
import { bootstrapApplication } from "@angular/platform-browser";
import { AppComponent } from "./app/app.component";

bootstrapApplication(AppComponent);

// styles.scss
@import "@bfrs/agentic-components/styles";

// angular.json alternative when package style exports are not resolved
"styles": [
  "node_modules/@bfrs/agentic-components/dist/style.css",
  "src/styles.scss"
]

// app.component.ts or feature.component.ts
import { CUSTOM_ELEMENTS_SCHEMA, Component } from "@angular/core";

@Component({
  selector: "app-root",
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: "./app.component.html"
})
export class AppComponent {}`,
              children: (
                <Stack gap={3}>
                  <Text variant="title">Angular setup</Text>
                  <Text variant="body-sm" tone="muted">Use `CUSTOM_ELEMENTS_SCHEMA` wherever Angular templates render `bfrs-*` elements.</Text>
                  <Text variant="body-sm" tone="muted">Keep the stylesheet import global so tokens and animations are available to every element.</Text>
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "Repository", description: "https://github.com/bfrs/agentic-components" },
            { name: "Showcase", description: PUBLIC_DOCS_URL },
            { name: "registry", description: "Use the public npmjs registry for install flows." },
            { name: "custom-elements", description: "Import `@bfrs/agentic-components/custom-elements` once to define all `bfrs-*` tags." },
            { name: "CUSTOM_ELEMENTS_SCHEMA", description: "Add it to standalone components or NgModules that use the custom elements." },
            { name: "@bfrs/agentic-components/styles", description: "Import once in a global stylesheet so CSS variables, Tailwind layers, and animations are available." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularUsagePage(): DocsPage {
  return {
    id: "usage",
    title: "Usage",
    category: "Start",
    description: "Use the registered `bfrs-*` custom elements directly in Angular templates.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Basic usage",
              description: "Pass simple values as attributes or property bindings, and listen to custom DOM events with Angular event binding.",
              code: `import { CUSTOM_ELEMENTS_SCHEMA, Component } from "@angular/core";

@Component({
  selector: "app-shipment-form",
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: \`
    <bfrs-paper>
      <bfrs-text variant="title">Create shipment</bfrs-text>
      <bfrs-input
        placeholder="Pickup pincode"
        [value]="pincode"
        (value-change)="pincode = $event.detail.value"
      ></bfrs-input>
      <bfrs-button (click)="continue()">Continue</bfrs-button>
    </bfrs-paper>
  \`
})
export class ShipmentFormComponent {
  pincode = "";

  continue() {
    // Submit shipment details.
  }
}`,
              children: (
                <Paper className="bfrs-max-w-sm">
                  <Stack gap={3}>
                    <Text variant="title">Create shipment</Text>
                    <Input placeholder="Pickup pincode" />
                    <Button>Continue</Button>
                  </Stack>
                </Paper>
              )
            },
            {
              title: "Live Angular slots and toast API",
              description: "Named slots preserve Angular-owned components, while the toast provider exposes imperative methods through ViewChild.",
              code: `<bfrs-page-header title="Orders">
  <bfrs-breadcrumbs slot="breadcrumb" [props]="{ items: breadcrumbs }"></bfrs-breadcrumbs>
  <bfrs-button slot="secondary-actions" variant="outline">Export</bfrs-button>
  <bfrs-button slot="actions">Create order</bfrs-button>
</bfrs-page-header>

<bfrs-workspace-header toggle (toggle-sidebar)="toggleSidebar()">
  <app-brand slot="brand"></app-brand>
  <app-global-search slot="search"></app-global-search>
  <app-user-actions slot="actions"></app-user-actions>
</bfrs-workspace-header>

<bfrs-toast-provider #toasts></bfrs-toast-provider>

// component.ts
@ViewChild("toasts") toasts!: ElementRef<HTMLElement & {
  success(message: string): string;
  danger(message: string): string;
  clear(): void;
}>;

save() {
  this.toasts.nativeElement.success("Order saved");
}`,
              children: (
                <Stack gap={3}>
                  <PageHeader
                    title="Orders"
                    breadcrumb={<Breadcrumbs items={[{ label: "Home", href: "#" }, { label: "Orders" }]} />}
                    actions={<Button>Create order</Button>}
                    secondaryActions={<Button variant="outline">Export</Button>}
                  />
                  <Text variant="body-sm" tone="muted">Angular components remain live when projected through named slots.</Text>
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "Element tags", description: "React component names are exposed as kebab-case custom elements, for example Button becomes `bfrs-button`." },
            { name: "Properties", description: "Use Angular property bindings for non-string values, arrays, objects, and booleans." },
            { name: "(click)", description: "Use native Angular event binding for activation and other DOM events. Do not pass React-style `onClick` in Angular templates." },
            { name: "Custom events", description: "Component callbacks emit kebab-case DOM events such as `value-change` with payloads in `event.detail`." },
            { name: "Slots", description: "Default and named slots keep projected Angular components live in the light DOM." },
            { name: "bfrs-toast-provider", description: "Provides toast, success, warning, danger, info, dismiss, and clear methods as the Angular equivalent of useToast." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularCustomizationPage(): DocsPage {
  return {
    id: "customization",
    title: "Customization",
    category: "Start",
    description: "Custom elements accept attributes, property bindings, classes, inline styles, and DOM events from Angular templates.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Global theme overrides",
              description: "Use the framework-neutral theme helper during app bootstrap or in an Angular root component.",
              code: `// main.ts or app.component.ts
import { applyAgenticTheme } from "@bfrs/agentic-components/theme";

applyAgenticTheme({
  colors: {
    primary: "#5B45C2",
    primaryHover: "#4C35AE",
    primarySoft: "#F1ECFB",
    primaryBorder: "#D8CEF9",
    borderFocus: "#5B45C2"
  },
  radius: {
    control: "14px",
    card: "18px"
  },
  shadows: {
    card: "0 12px 32px rgb(91 69 194 / 0.16)",
    elevated: "0 18px 48px rgb(91 69 194 / 0.22)"
  }
});

// Plain CSS works too on a BFRS scope:
.bfrs {
  --bfrs-color-primary: 251 51% 52%;
  --bfrs-radius-control: 14px;
  --bfrs-shadow-card: 0 12px 32px rgb(91 69 194 / 0.16);
}`,
              children: <DemoThemeOverrides />
            },
            {
              title: "Events and style overrides",
              description: "Use Angular bindings for event handlers and dynamic props. Use `class` and `style` for per-instance overrides.",
              code: `<bfrs-button
  class="bfrs-bg-accent hover:bfrs-bg-accent/90"
  (click)="save()"
>
  Save
</bfrs-button>

<bfrs-paper
  class="bfrs-cursor-pointer bfrs-border-border-strong bfrs-shadow-elevated"
  (click)="openDetails()"
>
  Clickable card
</bfrs-paper>

<div style="--bfrs-color-primary: 251 51% 52%">
  <bfrs-button>Token override</bfrs-button>
</div>`,
              children: (
                <Stack gap={4}>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-3">
                    <Button onClick={() => undefined} className="bfrs-bg-accent hover:bfrs-bg-accent/90">Save</Button>
                    <Paper onClick={() => undefined} className="bfrs-cursor-pointer bfrs-border-border-strong bfrs-shadow-elevated">Clickable card</Paper>
                  </div>
                  <Text variant="body-sm" tone="muted">
                    Custom element attributes map to the same component props documented for React unless a prop requires object, array, or function data.
                  </Text>
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "@bfrs/agentic-components/theme", description: "Framework-neutral subpath for theme helpers. Use it from Angular without importing React components." },
            { name: "applyAgenticTheme", description: "Applies CSS variable overrides to an explicit target or the first mounted BFRS scope." },
            { name: "colors / radius / shadows", description: "Typed groups cover theme-level colors, border radii, shadows, gradients, spacing, typography, and motion tokens." },
            { name: "class", description: "Preferred per-instance styling hook for Angular templates. Custom elements forward it to the React component as `className`." },
            { name: "style", description: "Use for dynamic values or CSS variable overrides. Prefer semantic props and tokens for normal styling." },
            { name: "(click) / (keydown) / (blur)", description: "Use normal Angular DOM event bindings for native events on custom elements." },
            { name: "kebab-case attributes", description: "Use kebab-case for attribute names in templates, for example `left-icon` or `error-message` where supported." },
            { name: "event.detail", description: "Custom events carry callback payloads in `event.detail`." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularLayoutPage(): DocsPage {
  return {
    id: "layout-primitives",
    title: "Layout primitives",
    category: "Foundations",
    description: "Use layout primitives as `bfrs-*` custom elements with normal Angular attributes, classes, and projected content.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Paper variants",
              code: `<bfrs-grid columns="4">
  <bfrs-paper variant="default">Default</bfrs-paper>
  <bfrs-paper variant="muted">Muted</bfrs-paper>
  <bfrs-paper variant="elevated">Elevated</bfrs-paper>
  <bfrs-paper variant="outlined">Outlined</bfrs-paper>
  <bfrs-paper variant="glass">Glass</bfrs-paper>
</bfrs-grid>`,
              children: (
                <Grid columns={4}>
                  {(["default", "muted", "elevated", "outlined", "glass"] as const).map((variant) => (
                    <Paper key={variant} variant={variant}><Text variant="body-sm">{variant}</Text></Paper>
                  ))}
                </Grid>
              )
            },
            {
              title: "Layout primitives",
              code: `<bfrs-container size="md">
  <bfrs-stack gap="4">
    <bfrs-box class="bfrs-rounded-lg bfrs-bg-surface-muted bfrs-p-4">Box</bfrs-box>
    <bfrs-grid columns="3">
      <bfrs-paper>One</bfrs-paper>
      <bfrs-paper variant="muted">Two</bfrs-paper>
      <bfrs-paper variant="outlined">Three</bfrs-paper>
    </bfrs-grid>
  </bfrs-stack>
</bfrs-container>`,
              children: (
                <Container size="md" className="bfrs-px-0">
                  <Stack gap={4}>
                    <Box className="bfrs-rounded-lg bfrs-bg-surface-muted bfrs-p-4">Box</Box>
                    <Grid columns={3}><Paper>One</Paper><Paper variant="muted">Two</Paper><Paper variant="outlined">Three</Paper></Grid>
                  </Stack>
                </Container>
              )
            },
            {
              title: "Typography",
              code: `<bfrs-text variant="display">Display</bfrs-text>
<bfrs-text variant="h1">Heading 1</bfrs-text>
<bfrs-text variant="title">Title</bfrs-text>
<bfrs-text variant="body">Body</bfrs-text>
<bfrs-text variant="label" tone="accent">Account Summary</bfrs-text>`,
              children: (
                <Stack gap={3}>
                  <Text variant="display">Display</Text>
                  <Text variant="h1">Heading 1</Text>
                  <Text variant="title">Title</Text>
                  <Text variant="body">Body text</Text>
                  <Text variant="label" tone="accent">Account Summary</Text>
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "class", description: "Use Angular `class` normally. The custom element forwards it into the inner React component." },
            { name: "html", description: "Use `html` only when you need trusted HTML content. Otherwise project children between the tags." },
            { name: "props", description: "Use `[props]` for structured or advanced component props." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularButtonsPage(): DocsPage {
  return {
    id: "buttons",
    title: "Button",
    category: "Actions",
    description: "Use `bfrs-button` and `bfrs-icon-button` with Angular DOM events and projected text.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Button variants and sizes",
              code: `<bfrs-button variant="primary" (click)="save()">Primary</bfrs-button>
<bfrs-button variant="secondary">Secondary</bfrs-button>
<bfrs-button variant="outline">Outline</bfrs-button>
<bfrs-button variant="ghost">Ghost</bfrs-button>
<bfrs-button variant="destructive">Destructive</bfrs-button>
<bfrs-button variant="link">Link</bfrs-button>

<bfrs-button size="sm">Small</bfrs-button>
<bfrs-button size="md">Medium</bfrs-button>
<bfrs-button size="lg">Large</bfrs-button>
<bfrs-button loading>Loading</bfrs-button>
<bfrs-button disabled>Disabled</bfrs-button>

<bfrs-button
  class="bfrs-bg-accent hover:bfrs-bg-accent/90"
  (click)="submit()"
>
  Custom action
</bfrs-button>`,
              children: (
                <Stack gap={4}>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-3">
                    <Button>Primary</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="destructive">Destructive</Button>
                    <Button variant="link">Link</Button>
                  </div>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-3">
                    <Button size="sm">Small</Button>
                    <Button size="md">Medium</Button>
                    <Button size="lg">Large</Button>
                    <Button loading>Loading</Button>
                    <Button disabled>Disabled</Button>
                    <IconButton aria-label="More actions" icon={<DotsThree size={22} weight="bold" />} variant="outline" />
                    <Button onClick={() => undefined} className="bfrs-bg-accent hover:bfrs-bg-accent/90">Custom action</Button>
                  </div>
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "(click)", description: "Use native Angular event binding for activation. Do not pass React-style `onClick`." },
            { name: "Projected text", description: "`<bfrs-button>Save</bfrs-button>` is supported. You can also use `label=\"Save\"`." },
            { name: "class", description: "Use Angular `class` for per-instance visual overrides." },
            { name: "aria-label", description: "Required for `bfrs-icon-button` because the visual icon is not enough for assistive technology." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularFormsPage(): DocsPage {
  return {
    id: "forms",
    title: "Forms",
    category: "Inputs",
    description: "Use attributes for simple form props, `[props]` for structured options, and custom events for value changes.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Text inputs",
              code: `<bfrs-input
  label="Email"
  type="email"
  placeholder="deeksha@example.com"
  [value]="email"
  (value-change)="email = $event.detail.value"
  (blur)="validateEmail()"
></bfrs-input>

<bfrs-input
  label="Pickup pincode"
  type="number"
  placeholder="122099"
  [value]="pickupPincode"
  (value-change)="pickupPincode = $event.detail.value"
></bfrs-input>

<bfrs-textarea
  placeholder="Add internal notes..."
  [value]="notes"
  (value-change)="notes = $event.detail.value"
></bfrs-textarea>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Input label="Email" type="email" placeholder="deeksha@example.com" />
                  <Input label="Pickup pincode" type="number" placeholder="122099" inputMode="numeric" />
                  <Textarea placeholder="Add internal notes..." />
                </div>
              )
            },
            {
              title: "Text inputs — sizes",
              description: "bfrs-input supports size=\"md\" (36px, default) and size=\"sm\" (28px). Font is 12px in both sizes.",
              code: `<!-- md — 36px (default) -->
<bfrs-input placeholder="Full name"></bfrs-input>
<bfrs-input value="Deeksha Gahlot"></bfrs-input>
<bfrs-input error placeholder="Error state"></bfrs-input>
<bfrs-input prefix="₹" placeholder="0.00"></bfrs-input>

<!-- sm — 28px -->
<bfrs-input size="sm" placeholder="Full name"></bfrs-input>
<bfrs-input size="sm" value="Deeksha Gahlot"></bfrs-input>
<bfrs-input size="sm" error placeholder="Error state"></bfrs-input>
<bfrs-input size="sm" prefix="₹" placeholder="0.00"></bfrs-input>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Stack gap={2}>
                    <Text variant="label" tone="muted">md — 36px (default)</Text>
                    <Input placeholder="Full name" />
                    <Input defaultValue="Deeksha Gahlot" />
                    <Input error placeholder="Error state" />
                    <Input prefix="₹" placeholder="0.00" />
                  </Stack>
                  <Stack gap={2}>
                    <Text variant="label" tone="muted">sm — 28px</Text>
                    <Input size="sm" placeholder="Full name" />
                    <Input size="sm" defaultValue="Deeksha Gahlot" />
                    <Input size="sm" error placeholder="Error state" />
                    <Input size="sm" prefix="₹" placeholder="0.00" />
                  </Stack>
                </div>
              )
            },
            {
              title: "Validated form with API checks",
              description: "Show client validation, simulated API validation, field-level errors below inputs, and toast feedback.",
              code: `import { CUSTOM_ELEMENTS_SCHEMA, Component } from "@angular/core";

type FieldErrors = Partial<Record<"businessName" | "email" | "pickupPincode", string>>;
type ToastTone = "success" | "danger";

@Component({
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: \`
    <form (submit)="submit($event)" class="bfrs-grid bfrs-gap-4">
      <bfrs-input
        label="Business name"
        required
        placeholder="Dee-doodles"
        [value]="businessName"
        [attr.error]="errors.businessName ? true : null"
        [attr.error-message]="errors.businessName || null"
        (value-change)="businessName = $event.detail.value">
      </bfrs-input>

      <bfrs-input
        label="Email"
        required
        type="email"
        helper-text="Used for shipment notifications."
        placeholder="ops@example.com"
        [value]="email"
        [attr.error]="errors.email ? true : null"
        [attr.error-message]="errors.email || null"
        (value-change)="email = $event.detail.value">
      </bfrs-input>

      <bfrs-input
        label="Pickup pincode"
        required
        helper-text="Serviceability is checked by the API on submit."
        placeholder="122099"
        suffix="IN"
        [value]="pickupPincode"
        [attr.error]="errors.pickupPincode ? true : null"
        [attr.error-message]="errors.pickupPincode || null"
        (value-change)="pickupPincode = $event.detail.value">
      </bfrs-input>

      <div class="bfrs-flex bfrs-flex-wrap bfrs-gap-2">
        <bfrs-button type="submit" [attr.loading]="submitting ? true : null">
          Validate form
        </bfrs-button>
        <bfrs-button type="button" variant="outline" (click)="reset()" [attr.disabled]="submitting ? true : null">
          Reset
        </bfrs-button>
      </div>
    </form>

    <bfrs-toast-manager
      placement="bottom-right"
      [props]="{ toasts: toasts }"
      (dismiss)="dismissToast($event.detail.id)">
    </bfrs-toast-manager>
  \`
})
export class ValidatedFormExample {
  businessName = "Dee-doodles";
  email = "ops@deedoodles.com";
  pickupPincode = "000000";
  errors: FieldErrors = {};
  submitting = false;
  toasts: Array<{ id: string; title: string; description: string; tone: ToastTone; duration: number; dismissible: boolean }> = [];

  async submit(event: Event) {
    event.preventDefault();

    const clientErrors = this.validateClient();
    if (Object.keys(clientErrors).length > 0) {
      this.errors = clientErrors;
      this.showToast("danger", "Validation failed", "Fix the highlighted fields before submitting.");
      return;
    }

    this.submitting = true;
    const pincodeError = await this.validatePickupPincode(this.pickupPincode);
    this.submitting = false;

    if (pincodeError) {
      this.errors = { pickupPincode: pincodeError };
      this.showToast("danger", "API validation failed", "The serviceability check returned a field error.");
      return;
    }

    this.errors = {};
    this.showToast("success", "Form validated", "The shipment form is ready to submit.");
  }

  reset() {
    this.businessName = "Dee-doodles";
    this.email = "ops@deedoodles.com";
    this.pickupPincode = "000000";
    this.errors = {};
  }

  private validateClient(): FieldErrors {
    const errors: FieldErrors = {};
    if (!this.businessName.trim()) errors.businessName = "Business name is required.";
    if (!this.email.trim()) errors.email = "Email is required.";
    else if (!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(this.email)) errors.email = "Enter a valid email address.";
    if (!/^\\d{6}$/.test(this.pickupPincode)) errors.pickupPincode = "Enter a 6-digit pickup pincode.";
    return errors;
  }

  private async validatePickupPincode(value: string) {
    await new Promise((resolve) => window.setTimeout(resolve, 500));
    return value === "000000" ? "Pickup is not serviceable for this pincode." : undefined;
  }

  private showToast(tone: ToastTone, title: string, description: string) {
    const id = \`toast-\${Date.now()}\`;
    this.toasts = [...this.toasts, { id, tone, title, description, duration: 2600, dismissible: true }].slice(-3);
  }

  dismissToast(id: string) {
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
  }
}`,
              children: <DemoValidatedForm />
            },
            {
              title: "Selection controls",
              code: `<bfrs-select
  placeholder="Choose business type"
  [props]="{ options: businessTypeOptions, value: businessType }"
  (value-change)="businessType = $event.detail.value"
></bfrs-select>

<bfrs-multi-select
  placeholder="Select channels"
  [props]="{ options: channelOptions, value: channels }"
  (value-change)="channels = $event.detail.value"
></bfrs-multi-select>

<bfrs-checkbox
  label="Accept terms"
  [checked]="accepted"
  (checked-change)="accepted = $event.detail.checked"
></bfrs-checkbox>

<bfrs-switch
  label="Enable notifications"
  [checked]="notifications"
  (checked-change)="notifications = $event.detail.checked"
></bfrs-switch>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Select options={[{ value: "registered", label: "Registered business" }, { value: "individual", label: "Individual seller" }]} placeholder="Choose business type" />
                  <SearchableSelect options={[{ value: "apparel", label: "Apparel" }, { value: "beauty", label: "Beauty" }]} placeholder="Search categories" />
                  <MultiSelect options={[{ value: "apparel", label: "Apparel" }, { value: "beauty", label: "Beauty" }, { value: "electronics", label: "Electronics" }]} defaultValue={["apparel", "electronics"]} placeholder="Select channels" />
                  <Checkbox label="Accept terms" />
                  <Switch label="Enable notifications" />
                </div>
              )
            },
            {
              title: "Selection controls — sizes",
              description: "All select-family components support size=\"md\" (36px, default) and size=\"sm\" (28px). Font is 12px in both sizes.",
              code: `<!-- md — 36px (default) -->
<bfrs-select [props]="{ options: options }" placeholder="Select type"></bfrs-select>
<bfrs-searchable-select [props]="{ options: options }" placeholder="Search categories"></bfrs-searchable-select>
<bfrs-multi-select [props]="{ options: options }" placeholder="Select channels"></bfrs-multi-select>
<bfrs-date-range-picker placeholder="Select date range"></bfrs-date-range-picker>

<!-- sm — 28px -->
<bfrs-select size="sm" [props]="{ options: options }" placeholder="Select type"></bfrs-select>
<bfrs-searchable-select size="sm" [props]="{ options: options }" placeholder="Search categories"></bfrs-searchable-select>
<bfrs-multi-select size="sm" [props]="{ options: options }" placeholder="Select channels"></bfrs-multi-select>
<bfrs-date-range-picker size="sm" placeholder="Select date range"></bfrs-date-range-picker>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Stack gap={2}>
                    <Text variant="label" tone="muted">md — 36px (default)</Text>
                    <Select options={businessSelectOptions} placeholder="Select business type" />
                    <SearchableSelect options={businessSelectOptions} placeholder="Search categories" onValueChange={() => undefined} />
                    <MultiSelect options={channelSelectOptions} placeholder="Select channels" onValueChange={() => undefined} />
                    <DateRangePicker placeholder="Select date range" onChange={() => undefined} />
                  </Stack>
                  <Stack gap={2}>
                    <Text variant="label" tone="muted">sm — 28px</Text>
                    <Select size="sm" options={businessSelectOptions} placeholder="Select business type" />
                    <SearchableSelect size="sm" options={businessSelectOptions} placeholder="Search categories" onValueChange={() => undefined} />
                    <MultiSelect size="sm" options={channelSelectOptions} placeholder="Select channels" onValueChange={() => undefined} />
                    <DateRangePicker size="sm" placeholder="Select date range" onChange={() => undefined} />
                  </Stack>
                </div>
              )
            },
            {
              title: "Selection — grouping, menu width & deferred apply",
              description: "Select-family components support size=\"md\" (36px) and size=\"sm\" (28px). menu-width is an attribute; group options via [props]; commit-mode=\"apply\" defers value-change until the user applies (with apply-label / cancel-label).",
              code: `<bfrs-select
  menu-width="auto"
  [props]="{ options: groupedOptions, value: method }"
  (value-change)="method = $event.detail.value"
></bfrs-select>

<bfrs-multi-select
  menu-width="auto"
  commit-mode="apply"
  apply-label="Apply"
  cancel-label="Cancel"
  [props]="{ options: channelOptions, values: channels }"
  (value-change)="channels = $event.detail.values"
  (apply)="channels = $event.detail.values"
></bfrs-multi-select>

<!-- group options by adding a \`group\` to each option -->
groupedOptions = [
  { value: "cod", label: "Cash on Delivery", group: "Payment" },
  { value: "dom", label: "Domestic", group: "Shipping" }
];`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Stack gap={2}>
                    <Select options={businessSelectOptions} placeholder="Select business type" />
                    <Select options={groupedSelectOptions} menuWidth="auto" placeholder="Grouped · wide menu" />
                  </Stack>
                  <Stack gap={2}>
                    <MultiSelect options={channelSelectOptions} commitMode="apply" defaultValue={["amazon"]} placeholder="Apply mode" />
                    <MultiSelect options={groupedSelectOptions} menuWidth="auto" defaultValue={["cod", "dom"]} placeholder="Grouped · wide menu" />
                  </Stack>
                </div>
              )
            },
            {
              title: "Select & SearchableSelect — grouping & menu width",
              description: "Select triggers use one Figma-matched 28px height. Options can be grouped under headings, and the menu can match the trigger or grow wider (menu-width: \"trigger\" | \"auto\" | number).",
              code: `<bfrs-select [props]="{ options: options }"></bfrs-select>

<!-- grouped options (add a \`group\` to each option) -->
<bfrs-select
  menu-width="auto"
  [props]="{ options: groupedOptions }"
></bfrs-select>

<bfrs-select menu-width="320" [props]="{ options: options }"></bfrs-select>
<bfrs-searchable-select menu-width="auto" [props]="{ options: groupedOptions }"></bfrs-searchable-select>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Stack gap={2}>
                    <Select options={businessSelectOptions} placeholder="Select business type" />
                  </Stack>
                  <Stack gap={2}>
                    <Select options={groupedSelectOptions} placeholder="Grouped options" />
                    <Select options={businessSelectOptions} menuWidth={320} placeholder="menuWidth: 320px" />
                    <SearchableSelect menuWidth="auto" options={groupedSelectOptions} placeholder="Searchable + grouped" searchPlaceholder="Search…" onValueChange={() => undefined} />
                  </Stack>
                </div>
              )
            },
            {
              title: "MultiSelect — filter variant, apply mode & footer button variants",
              description: "MultiSelect uses the same 28px trigger as Select. variant=\"filter\" removes the search box for filter dropdowns. Grouped options, wider menus, deferred apply, footer variants, and custom footers are supported.",
              code: `<!-- deferred commit: value-change fires ONLY on apply -->
<bfrs-multi-select
  commit-mode="apply"
  apply-variant="primary"
  cancel-variant="secondary"
  apply-label="Apply"
  cancel-label="Cancel"
  menu-width="auto"
  [props]="{ options: channelOptions, values: channels }"
  (apply)="channels = $event.detail.values"
></bfrs-multi-select>

<!-- filter variant: no search input -->
<bfrs-multi-select
  variant="filter"
  [props]="{ options: channelOptions, values: channels }"
  (value-change)="channels = $event.detail.values"
></bfrs-multi-select>

<!-- fully custom footer -->
<bfrs-multi-select [props]="{ options: options }">
  <div slot="footer">…</div>
</bfrs-multi-select>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Stack gap={2}>
                    <MultiSelect options={channelSelectOptions} defaultValue={["amazon", "shopify"]} placeholder="Select channels" onValueChange={() => undefined} />
                    <MultiSelect options={groupedSelectOptions} menuWidth="auto" defaultValue={["cod", "dom"]} placeholder="Grouped · wide menu" onValueChange={() => undefined} />
                    <MultiSelect options={channelSelectOptions} variant="filter" placeholder="Filter without search" onValueChange={() => undefined} />
                  </Stack>
                  <Stack gap={2}>
                    <DemoMultiSelectApply />
                    <MultiSelect options={channelSelectOptions} commitMode="apply" applyVariant="outline" cancelVariant="ghost" placeholder="apply · outline / ghost" onValueChange={() => undefined} />
                    <MultiSelect options={channelSelectOptions} commitMode="apply" applyVariant="secondary" cancelVariant="outline" applyLabel="Save" cancelLabel="Cancel" placeholder="apply · secondary / outline" onValueChange={() => undefined} />
                    <MultiSelect
                      options={channelSelectOptions}
                      placeholder="Custom footer"
                      onValueChange={() => undefined}
                      footer={
                        <div className="bfrs-flex bfrs-items-center bfrs-justify-between bfrs-px-1">
                          <Button variant="link" size="xs">Saved filters</Button>
                          <Button variant="primary" size="xs">Done</Button>
                        </div>
                      }
                    />
                  </Stack>
                </div>
              )
            },
            {
            title: "TagsInput / TagInput — chip entry",
            description: "Controlled chip input for free-form order tags with optional existing-tag suggestions. It is creatable by default, unlike MultiSelect.",
            code: `<bfrs-tag-input
  placeholder="Add Tag"
  [props]="{ values: orderTags, suggestions: allTags, creatable: true }"
  (value-change)="orderTags = $event.detail.values">
</bfrs-tag-input>`,
              children: <DemoTagInput />
            },
            {
              title: "Checkbox, Radio & Switch — sizes & states",
              description: "Boolean and single-choice controls with their sizes and states.",
              code: `<bfrs-checkbox size="sm" label="sm" checked></bfrs-checkbox>
<bfrs-checkbox size="md" label="md" checked></bfrs-checkbox>
<bfrs-checkbox size="lg" label="lg" checked></bfrs-checkbox>
<bfrs-checkbox label="Indeterminate" checked="indeterminate"></bfrs-checkbox>
<bfrs-checkbox label="Disabled" disabled></bfrs-checkbox>

<bfrs-radio
  [props]="{
    options: [
      { value: 'registered', label: 'Registered business', description: 'Company GST and business KYC are available.' },
      { value: 'individual', label: 'Individual seller', description: 'Use personal details for seller verification.' }
    ],
    value: businessType
  }"
  (value-change)="businessType = $event.detail.value">
</bfrs-radio>
<bfrs-switch label="Enable notifications" checked></bfrs-switch>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                  <Stack gap={2}>
                    <div className="bfrs-flex bfrs-items-center bfrs-gap-4">
                      <Checkbox size="sm" label="sm" defaultChecked />
                      <Checkbox size="md" label="md" defaultChecked />
                      <Checkbox size="lg" label="lg" defaultChecked />
                    </div>
                    <Checkbox label="Partially selected" checked="indeterminate" />
                    <Checkbox label="Disabled" disabled />
                  </Stack>
                  <Stack gap={2}>
                    <Radio options={businessRadioOptions} defaultValue="registered" />
                    <Switch label="Enable notifications" defaultChecked />
                    <Switch label="Auto-assign couriers" />
                    <Switch label="Disabled" disabled />
                  </Stack>
                </div>
              )
            },
            {
              title: "Additional form controls",
              description: "Reusable controls for card radios, segmented chips, tag chips, quantities, free-text suggestions, uploads, and field-level reveal.",
              code: `<bfrs-option-card-group
  [props]="{ options: fulfillmentOptions, value: fulfillmentMode }"
  (value-change)="fulfillmentMode = $event.detail.value">
</bfrs-option-card-group>

<bfrs-selectable-chip-group
  multiple
  [props]="{ options: channelOptions, value: channels }"
  (value-change)="channels = $event.detail.value">
</bfrs-selectable-chip-group>

<bfrs-number-stepper [value]="quantity" min="1" max="10" (value-change)="quantity = $event.detail.value"></bfrs-number-stepper>
<bfrs-suggest-input [props]="{ suggestions: citySuggestions, value: city }" (value-change)="city = $event.detail.value"></bfrs-suggest-input>
<bfrs-tag-input [props]="{ values: orderTags, suggestions: allTags }" (value-change)="orderTags = $event.detail.values"></bfrs-tag-input>
<bfrs-file-dropzone title="Upload invoice" (files-change)="files = $event.detail.files"></bfrs-file-dropzone>
<bfrs-reveal-field label="Masked account field" value="9876543210"></bfrs-reveal-field>`,
              children: <DemoAdditionalFormControls />
            },
            {
              title: "Range and color controls",
              description: "Use these tags instead of raw range inputs, color inputs, or custom palette buttons.",
              code: `<bfrs-slider
  label="Confidence threshold"
  min="0"
  max="100"
  step="5"
  [value]="confidence"
  (value-change)="confidence = $event.detail.value"
></bfrs-slider>

<bfrs-color-picker
  label="Brand color"
  [value]="brandColor"
  (value-change)="brandColor = $event.detail.value"
></bfrs-color-picker>

<bfrs-color-swatch-group
  label="Palette"
  [props]="{ options: paletteOptions, value: palette }"
  (value-change)="palette = $event.detail.value"
></bfrs-color-swatch-group>`,
              children: <DemoRangeAndColorControls />
            },
            {
              title: "Date range picker",
              code: `<bfrs-date-range-picker
  [props]="{ value: dateRange }"
  max-range-days="365"
  (date-range-change)="dateRange = $event.detail"
></bfrs-date-range-picker>`,
              children: <DemoDateRangePicker />
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "value-change", description: "Inputs, selects, sliders, color pickers, swatch groups, radio groups, tabs, and composer controls emit new values in `$event.detail.value`." },
            { name: "checked-change", description: "Checkbox and Switch emit `{ checked }` in `$event.detail`." },
            { name: "[props]", description: "Use property binding for arrays, objects, booleans, and date-range values." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularFeedbackPage(): DocsPage {
  return {
    id: "feedback",
    title: "Feedback",
    category: "Feedback",
    description: "Status, loading, and empty-state components are available as custom elements.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Status components",
              code: `<bfrs-chip tone="primary">Verified</bfrs-chip>
<bfrs-chip tone="success" variant="outline">Live</bfrs-chip>
<bfrs-badge tone="info">New</bfrs-badge>
<bfrs-alert tone="info" title="Info">A neutral informational state.</bfrs-alert>
<bfrs-alert tone="success" title="Success">Details fetched successfully.</bfrs-alert>
<bfrs-alert tone="warning" title="Warning">Review required.</bfrs-alert>
<bfrs-alert tone="danger" title="Payment failed" message="Update payment details and retry."></bfrs-alert>
<bfrs-alert tone="warning" title="Review required"></bfrs-alert>`,
              children: (
                <Stack gap={4}>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-2">
                    <Chip tone="primary">Verified</Chip>
                    <Chip tone="success" variant="outline">Live</Chip>
                    <Badge tone="info">New</Badge>
                  </div>
                  <div className="bfrs-grid bfrs-gap-3 md:bfrs-grid-cols-2">
                    <Alert tone="info" title="Info">A neutral informational state.</Alert>
                    <Alert tone="success" title="Success">Details fetched successfully.</Alert>
                    <Alert tone="warning" title="Warning">Review required.</Alert>
                    <Alert tone="danger" title="Payment failed" message="Update payment details and retry." />
                    <Alert tone="warning" title="Review required" />
                  </div>
                </Stack>
              )
            },
            {
              title: "Alert sizes",
              description: "Alert supports sm, md (default), and lg sizes.",
              code: `<bfrs-alert size="xs" tone="info" title="XSmall">Ultra-compact inline alert.</bfrs-alert>
<bfrs-alert size="sm" tone="success" title="Small">Compact alert.</bfrs-alert>
<bfrs-alert size="md" tone="warning" title="Medium">Default size.</bfrs-alert>
<bfrs-alert size="lg" tone="info" title="Large">Spacious alert.</bfrs-alert>`,
              children: (
                <Stack gap={3}>
                  <Alert size="sm" tone="success" title="Small">Compact alert for dense layouts.</Alert>
                  <Alert size="md" tone="warning" title="Medium">Default alert size.</Alert>
                  <Alert size="lg" tone="info" title="Large">Spacious alert for prominent messages.</Alert>
                </Stack>
              )
            },
            {
              title: "Sizes",
              description: "Chip and Badge both support xs, sm (default), md, and lg sizes.",
              code: `<bfrs-chip size="xs" tone="primary">xs</bfrs-chip>
<bfrs-chip size="sm" tone="primary">sm</bfrs-chip>
<bfrs-chip size="md" tone="primary">md</bfrs-chip>
<bfrs-chip size="lg" tone="primary">lg</bfrs-chip>
<bfrs-badge size="xs" tone="success">xs</bfrs-badge>
<bfrs-badge size="sm" tone="success">sm</bfrs-badge>
<bfrs-badge size="md" tone="success">md</bfrs-badge>
<bfrs-badge size="lg" tone="success">lg</bfrs-badge>`,
              children: (
                <Stack gap={3}>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-2">
                    <Chip size="xs" tone="primary">xs</Chip>
                    <Chip size="sm" tone="primary">sm</Chip>
                    <Chip size="md" tone="primary">md</Chip>
                    <Chip size="lg" tone="primary">lg</Chip>
                  </div>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-2">
                    <Badge size="xs" tone="success">xs</Badge>
                    <Badge size="sm" tone="success">sm</Badge>
                    <Badge size="md" tone="success">md</Badge>
                    <Badge size="lg" tone="success">lg</Badge>
                  </div>
                </Stack>
              )
            },
            {
              title: "Loading and empty states",
              code: `<bfrs-skeleton variant="text"></bfrs-skeleton>
<bfrs-skeleton variant="card" animated="false"></bfrs-skeleton>
<bfrs-spinner label="Loading"></bfrs-spinner>
<bfrs-empty-state title="No notifications yet" description="Important updates will appear here."></bfrs-empty-state>`,
              children: (
                <Stack gap={4}>
                  <div className="bfrs-grid bfrs-gap-3 md:bfrs-grid-cols-3"><Skeleton variant="text" /><Skeleton variant="card" animated={false} /><Skeleton variant="avatar" /></div>
                  <Spinner />
                  <EmptyState icon={<Bell size={24} />} title="No notifications yet" description="Important updates will appear here." />
                </Stack>
              )
            },
            {
              title: "Progress bars",
              code: `<bfrs-progress-bar value="64" label="Upload progress" show-value="true"></bfrs-progress-bar>
<bfrs-progress-bar value="82" tone="success" size="lg"></bfrs-progress-bar>
<bfrs-progress-bar indeterminate="true" label="Syncing changes…"></bfrs-progress-bar>`,
              children: <DemoProgressBar />
            },
            {
              title: "Toast manager",
              code: `<bfrs-toast-manager
  placement="bottom-right"
  [props]="{ toasts: toasts }"
  (dismiss)="dismissToast($event.detail.id)">
</bfrs-toast-manager>

<bfrs-button (click)="pushToast('success', 'Action completed')">Save</bfrs-button>`,
              children: <DemoToastManager />
            },
            {
              title: "Callouts",
              code: `<bfrs-info-card
  tone="info"
  title="Courier allocation is ready"
  description="Review serviceability before assigning the shipment.">
</bfrs-info-card>

<bfrs-tip>Recommended</bfrs-tip>
<bfrs-tip tone="success">Verified</bfrs-tip>`,
              children: <DemoCalloutPatterns />
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "value / max / show-value", description: "ProgressBar fill is value/max as a percentage. Use show-value to render the percentage; add tone and size attributes for variants." },
            { name: "Alert variants", description: "`tone=\"info\" | \"success\" | \"warning\" | \"danger\"` controls alert color, border, and icon. Warning uses the shared 11px triangle icon with `#D97706`, `rgba(217, 119, 6, 0.08)` background, and `rgba(217, 119, 6, 0.2)` border." },
            { name: "Alert content", description: "Use title plus default slot content, or title plus message/description attributes. Omit message/description and slot content for a single-line alert." },
            { name: "indeterminate", description: "Set indeterminate=\"true\" for an animated track when the duration is unknown." },
            { name: "bfrs-remove", description: "Removable chips emit `bfrs-remove` with the chip label." },
            { name: "dismiss", description: "Toast manager emits `dismiss` with `{ id }`." },
            { name: "class", description: "Use Angular class bindings for layout and visual overrides." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularOverlaysPage(): DocsPage {
  return {
    id: "overlays",
    title: "Overlays",
    category: "Overlays",
    description: "Use custom events to control overlay state from Angular.",
    content: (
      <ComponentPage
        examples={[
          {
            title: "Modal, drawer, dropdown, tooltip, popover",
            code: `<bfrs-modal
  title="Confirm"
  [attr.open]="confirmOpen || null"
  (open-change)="confirmOpen = $event.detail.open"
  (close)="confirmOpen = false"
>
  Review the details before continuing.
</bfrs-modal>

<bfrs-drawer title="Filters" placement="right" (close)="filtersOpen = false">
  Drawer content
</bfrs-drawer>

<bfrs-dropdown
  trigger-label="Actions"
  [props]="{ items: actionItems }"
  (dropdown-select)="runAction($event.detail.item)"
></bfrs-dropdown>

<bfrs-tooltip content="White tooltip" variant="white" trigger-label="Hover"></bfrs-tooltip>`,
            children: (
              <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-3">
                <Modal title="Confirm account details" trigger={<Button>Open modal</Button>}><Text variant="body-sm" tone="muted">Review the details before continuing.</Text></Modal>
                <Drawer title="Filters" trigger={<Button variant="outline">Open drawer</Button>}><Text variant="body-sm" tone="muted">Drawer content.</Text></Drawer>
                <Dropdown trigger={<IconButton aria-label="Open menu" icon={<DotsThree size={22} weight="bold" />} variant="outline" />} items={[{ label: "Edit" }, { label: "Delete", destructive: true }]} />
                <Tooltip content="White tooltip surface." variant="white"><Button variant="outline">White tooltip</Button></Tooltip>
                <Popover trigger={<Button variant="secondary">Open popover</Button>}><Text variant="title">Popover content</Text></Popover>
              </div>
            )
          }
        ]}
      />
    )
  };
}

function createAngularDataDisplayPage(): DocsPage {
  return {
    id: "data-display",
    title: "Data display",
    category: "Data display",
    description: "Pass table columns and rows through `[props]`, then listen to table events from Angular.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "KPI cards",
              code: `<bfrs-kpi-card label="Total orders" value="05"></bfrs-kpi-card>
<bfrs-kpi-card label="Delivered" value="128" accent="success"></bfrs-kpi-card>
<bfrs-kpi-card label="NDR pending" value="12" accent="warning"></bfrs-kpi-card>`,
              children: <DemoKpiCard />
            },
            {
              title: "DataTable",
              code: `<bfrs-data-table
  [props]="{ data: rows, columns: columns, selection: true }"
  [selected-row-ids]="selectedRowIds"
  table-class-name="bfrs-min-w-full"
  header-class-name="bfrs-sticky bfrs-top-0"
  row-class-name="bfrs-cursor-pointer"
  cell-class-name="bfrs-align-middle"
  (row-click)="openCustomer($event.detail.row)"
  (selected-row-ids-change)="selectedRowIds = $event.detail.ids"
  (sort-change)="sorting = $event.detail.sorting"
></bfrs-data-table>

<bfrs-table-pagination
  page="2"
  total-pages="6"
  (page-change)="page = $event.detail.page"
></bfrs-table-pagination>`,
              children: (
                <Stack gap={4}>
                  <DemoDataTable />
                  <TablePagination page={2} totalPages={6} onPageChange={() => undefined} />
                </Stack>
              )
            },
            {
              title: "DataTable helper elements",
              code: `<bfrs-table-toolbar title="Orders" total-label="128 orders">
  <bfrs-input slot="search" placeholder="Search orders"></bfrs-input>
  <bfrs-table-column-visibility
    slot="column-visibility"
    [props]="{ columns, visibleColumnIds }"
    (visible-column-ids-change)="visibleColumnIds = $event.detail.visibleColumnIds">
  </bfrs-table-column-visibility>
  <bfrs-button slot="actions">Create order</bfrs-button>
</bfrs-table-toolbar>

<bfrs-table-bulk-actions
  [selected-count]="selectedRowIds.length"
  (clear-selection)="selectedRowIds = []">
  <bfrs-button slot="actions" variant="outline">Export selected</bfrs-button>
</bfrs-table-bulk-actions>`,
              children: (
                <Stack gap={3}>
                  <TableToolbar title="Orders" totalLabel="128 orders" search={<Input size="sm" placeholder="Search orders" />} actions={<Button size="sm">Create order</Button>} />
                  <TableBulkActions selectedCount={2} actions={<Button size="sm" variant="outline">Export selected</Button>} onClearSelection={() => undefined} />
                </Stack>
              )
            },
            {
              title: "Saved views selector (localStorage + API)",
              description: "Use the same free-form screen name on the selector and save-view element. Pass saveApi through props when the view should use Shiprocket list/get/update/delete endpoints and mirror to localStorage.",
              code: `// component class
screenName = "orders_all";
saveApi = { baseUrl: environment.platformBaseUrl, viewType: "orders", tab: "all" };
filters = [{ field: "channel", op: "equals", value: "Shopify_1" }];

applySavedView(view: any) {
  if (!view) return;
  this.visibleColumnIds = view.columns
    .filter((column: any) => column.visible)
    .map((column: any) => column.key);
  this.filters = view.filters ?? [];
}

// template
<bfrs-list-page-layout title="Orders">
  <bfrs-table-view-selector
    slot="filters"
    [screen-name]="screenName"
    [props]="{ saveApi: saveApi }"
    size="sm"
    (value-change)="applySavedView($event.detail.view)"
    (primary-view-change)="applySavedView($event.detail.view)">
  </bfrs-table-view-selector>

  <bfrs-select
    slot="filters"
    size="sm"
    [props]="{ options: statusOptions }"
    [value]="status"
    (value-change)="status = $event.detail.value">
  </bfrs-select>

  <bfrs-table-save-view
    slot="filters"
    [screen-name]="screenName"
    [props]="{
      saveApi: saveApi,
      viewType: 'orders',
      columns: visibilityColumns,
      visibleColumnIds: visibleColumnIds,
      filters: filters,
      filtersCount: filters.length
    }"
    (saved-view)="applySavedView($event.detail.view)">
  </bfrs-table-save-view>

  <bfrs-data-table
    slot="table"
    [props]="{ data: rows, columns: columns, visibleColumnIds: visibleColumnIds }">
  </bfrs-data-table>
</bfrs-list-page-layout>`,
              children: <DemoSavedViewsSelector />
            },
            {
              title: "Standalone table (no toolbar)",
              description: "With no toolbar stacked on top, the table rounds its own header — all four corners use the 8px card radius.",
              code: `<bfrs-data-table
  [props]="{ data: rows, columns: columns }">
</bfrs-data-table>`,
              children: <DemoStandaloneDataTable />
            },
            {
              title: "Empty state (no data)",
              description: "With an empty data array, the built-in empty view is a two-line message and ships with a default. Override empty-title / empty-description for custom copy.",
              code: `<!-- Default two-line empty message -->
<bfrs-data-table [props]="{ data: [], columns: columns }"></bfrs-data-table>

<!-- Custom copy -->
<bfrs-data-table
  empty-title="No orders yet"
  empty-description="New orders will show up here as they come in."
  [props]="{ data: [], columns: columns }">
</bfrs-data-table>`,
              children: <DemoEmptyDataTable />
            },
            {
              title: "Fixed height — sticky header & footer, scrollable rows",
              description: "Set max-height to cap the scroll region: the header stays pinned at the top, indexed pagination stays fixed as a footer, and only the rows scroll. The footer's 'Items per page' label and item suffix are customizable text.",
              code: `<bfrs-data-table
  max-height="300"
  [props]="{
    data: rows,
    columns: columns,
    selection: true,
    pagination: {
      page: page,
      totalPages: 10,
      variant: 'indexed',
      pageSize: pageSize,
      pageSizeOptions: [10, 15, 20, 25, 50],
      pageSizeControl: 'menu',
      itemsPerPageLabel: 'Items per page:',
      itemLabel: 'Orders'
    }
  }"
  (page-change)="page = $event.detail.page"
  (page-size-change)="pageSize = $event.detail.pageSize">
</bfrs-data-table>`,
              children: <DemoFixedHeightDataTable />
            },
            {
              title: "Fixed height with column visibility & save",
              description: "The same fixed-height table plus the Columns menu in a toolbar: required columns (disabled) can't be hidden, Show all / Reset live in the menu, a Save + Discard cluster appears only once the column selection changes, and the final actions column stays pinned to the right. This example uses the simple Prev/Next pagination variant inside bfrs-data-table.",
              code: `<bfrs-table-toolbar title="Orders">
  <ng-container slot="actions">
    <bfrs-button *ngIf="dirty" size="sm" (click)="visibleColumnIds = savedColumnIds">↺</bfrs-button>
    <bfrs-button *ngIf="dirty" size="sm" (click)="savedColumnIds = visibleColumnIds">Save</bfrs-button>
    <bfrs-table-column-visibility
      slot="column-visibility"
      [props]="{ columns: visibilityColumns, visibleColumnIds }"
      (visible-column-ids-change)="visibleColumnIds = $event.detail.visibleColumnIds"
      (reset)="visibleColumnIds = savedColumnIds">
    </bfrs-table-column-visibility>
  </ng-container>
</bfrs-table-toolbar>

<bfrs-data-table
  max-height="300"
  sticky-row-actions
  row-actions-header="Actions"
  row-actions-width="88"
  [props]="{
    data: rows,
    columns,
    selection: true,
    visibleColumnIds,
    rowActions: [{ id: 'open', display: 'icon', iconName: 'DotsThree', ariaLabel: 'Row actions' }],
    pagination: { ...pagination, variant: 'simple' }
  }"
  (page-change)="page = $event.detail.page">
</bfrs-data-table>`,
              children: <DemoFixedHeightColumnsDataTable />
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "[props]", description: "Property binding carries non-serializable/structured input: `data`, `columns`, `visibleColumnIds`, `columnOrder`, `pagination`, `leadingColumn`, `rowActions`, `rowClassRules`, `rowIdKey`, and the pagination object." },
            { name: "Attributes: max-height / min-width / sticky-header", description: "Plain attributes. `max-height` caps the scroll region (rows scroll, header stays pinned); pair with the `pagination` prop for a fixed footer. `min-width` forces horizontal scroll. Table density is fixed to the Figma default." },
            { name: "Attributes: enable-sorting / enable-column-pinning / enable-column-reordering", description: "Default true; set to false to disable sortable headers, the Pin/Unpin controls, or header drag-reordering." },
            { name: "selection / selected-row-ids", description: "`selection` attribute turns on row checkboxes; bind `[selected-row-ids]` and listen to `selected-row-ids-change` (`$event.detail.ids`)." },
            { name: "visible-column-ids", description: "Bind `[visible-column-ids]` to control which columns render; pair with `bfrs-table-column-visibility`. The table emits `visible-column-ids-change`." },
            { name: "cellType / cellConfig", description: "Declarative cell renderers (since Angular/HTML can't pass JS `cell` functions): text, chips, badges, images, avatars, links, buttons, actions, and registered custom elements." },
            { name: "cellType: 'button' | 'buttons' | 'actions'", description: "Inline button(s) or an actions menu. Each action: id, label, variant, size, display, iconName, ariaLabel, disabled, disabledKey, destructive." },
            { name: "Events: sort-change / row-click / cell-action / cell-event", description: "Row-aware payloads in `$event.detail`. `cell-action` = { actionId, columnId, row, rowId, rowIndex } and does NOT also fire `row-click`." },
            { name: "Events: pinned-column-count-change / column-order-change / pin-leading-column-change", description: "Emitted as users pin or drag-reorder columns; mirror them back into `[props]` for controlled state." },
            { name: "Events: page-change / page-size-change", description: "Pagination navigation and page-size selection; payloads in `$event.detail`." },
            { name: "table-class-name / header-class-name / row-class-name / cell-class-name", description: "Attribute-based class overrides for the table, header, rows, and cells. `rowClassRules` (via [props]) covers conditional row classes." },
            { name: "States: loading / error / empty", description: "`loading` attribute renders row skeletons; pass an `error` message or `empty-title` / `empty-description` for the built-in states." },
            { name: "bfrs-table-toolbar", description: "Header bar with `title` / `total-label`, a `search` slot, a `column-visibility` slot, and an `actions` slot (Save / refresh / create). Round its top and set `rounded-t-none` on the table to read as one card." },
            { name: "bfrs-table-column-visibility", description: "[props]={ columns, visibleColumnIds }. Each column: { id, label, disabled? } — `disabled` marks a required, always-visible column. Emits `visible-column-ids-change`; `reset` and `show-all` drive the footer buttons." },
            { name: "bfrs-table-view-selector", description: "`screen-name` scopes saved views in localStorage and the element stays hidden until that screen has at least one saved view. Pass `saveApi` via [props] to call backend list/get/update/delete first and mirror to localStorage. Place it in the list-page `filters` slot, not in `bfrs-table-toolbar`. Emits `value-change`, `view-select`, `primary-view-change`, `rename-view`, `delete-view`, and `reset-saved-filters`." },
            { name: "bfrs-table-save-view screen-name", description: "When `screen-name` is provided and no `onSave` prop is supplied, the custom element persists the SaveViewPayload to the matching localStorage view store and emits `saved-view` after save." },
            { name: "bfrs-table-pagination", description: "page / total-pages with `page-change`; `variant` = indexed | simple. page-size cluster via pageSize + `page-size-change`. `page-size-control` = select | menu. Customizable text: `items-per-page-label` and `item-label`. `show-page-navigation` = false renders only the size cluster. For bfrs-data-table attributes, use `pagination-variant` when constructing pagination from page / total-pages." },
            { name: "bfrs-table-bulk-actions", description: "Selection-aware action bar: bind `[selected-count]`, expose bulk operations in the `actions` slot, and clear via `clear-selection`." }
          ]}
        />
        <ComponentPage
          examples={[
            {
              title: "Table component",
              description: "Use bfrs-table, bfrs-thead, bfrs-tbody, bfrs-tfoot, bfrs-tr, bfrs-th, and bfrs-td for custom Angular table layouts where DataTable is too prescriptive.",
              code: `<bfrs-table density="default" aria-label="Orders">
  <bfrs-thead sticky>
    <bfrs-tr>
      <bfrs-th>Order ID</bfrs-th>
      <bfrs-th>Customer</bfrs-th>
      <bfrs-th align="right">Amount</bfrs-th>
      <bfrs-th>Status</bfrs-th>
    </bfrs-tr>
  </bfrs-thead>
  <bfrs-tbody>
    <bfrs-tr *ngFor="let row of rows" on-click (click)="select(row.id)">
      <bfrs-td>{{ row.id }}</bfrs-td>
      <bfrs-td>{{ row.customer }}</bfrs-td>
      <bfrs-td align="right">{{ row.amount }}</bfrs-td>
      <bfrs-td>{{ row.status }}</bfrs-td>
    </bfrs-tr>
  </bfrs-tbody>
  <bfrs-tfoot>
    <bfrs-tr>
      <bfrs-th>Total</bfrs-th>
      <bfrs-th></bfrs-th>
      <bfrs-th align="right">₹4,70,450</bfrs-th>
      <bfrs-th></bfrs-th>
    </bfrs-tr>
  </bfrs-tfoot>
</bfrs-table>`,
              children: (
                <Stack gap={4}>
                  <DemoPrimitiveTable />
                  <DemoCompactTable />
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "bfrs-table density", description: "compact | default | comfortable — propagated automatically to all descendant bfrs-th and bfrs-td cells." },
            { name: "bfrs-table max-height", description: "CSS max-height on the scroll container. Pair with bfrs-thead sticky to keep the header pinned." },
            { name: "bfrs-thead sticky", description: "Boolean attribute — sticks the header row at the top of the scroll container (position: sticky; z-index: 30)." },
            { name: "bfrs-tr (click)", description: "Bind Angular (click) normally. Add the on-click attribute (boolean) to enable cursor-pointer styling: <bfrs-tr on-click (click)=\"select(row)\">." },
            { name: "bfrs-th / bfrs-td align", description: "left (default), center, or right text alignment." },
            { name: "No colspan", description: "Custom elements with display:table-cell do not support the HTML colspan attribute. Use native <td>/<th> inside bfrs-tr for spanning cells." },
            { name: "Hover", description: "bfrs-tr listens for mouseenter/mouseleave and updates bfrs-td cell backgrounds — no CSS group-hover needed across shadow boundaries." }
          ]}
        />
      </Stack>
    )
  };
}

function createAngularNavigationPage(): DocsPage {
  return {
    id: "navigation",
    title: "Navigation",
    category: "Navigation",
    description: "Navigation components accept structured item arrays through `[props]`.",
    content: (
      <ComponentPage
        examples={[
          {
            title: "Tabs and breadcrumbs",
            code: `<bfrs-breadcrumbs
  [props]="{ items: breadcrumbItems }"
  (breadcrumb-click)="handleBreadcrumb($event.detail.item)"
></bfrs-breadcrumbs>

<bfrs-tabs
  [props]="{ items: tabItems, value: activeTab }"
  (value-change)="activeTab = $event.detail.value"
>
  <bfrs-badge slot="tab-overview-badge" count="4"></bfrs-badge>
  <app-overview slot="tab-overview-content"></app-overview>
  <app-settings slot="tab-settings-content"></app-settings>
</bfrs-tabs>

<bfrs-tabs value="overview">
  <bfrs-tabs-list>
    <bfrs-tabs-trigger value="overview">Overview</bfrs-tabs-trigger>
    <bfrs-tabs-trigger value="settings">Settings</bfrs-tabs-trigger>
  </bfrs-tabs-list>
  <bfrs-tabs-content value="overview"><app-overview></app-overview></bfrs-tabs-content>
  <bfrs-tabs-content value="settings"><app-settings></app-settings></bfrs-tabs-content>
</bfrs-tabs>`,
            children: (
              <Stack gap={4}>
                <Breadcrumbs items={[{ label: "Home", href: "#" }, { label: "Onboarding", href: "#" }, { label: "Summary" }]} />
                <Tabs defaultValue="overview" items={[{ value: "overview", label: "Overview", content: <Text variant="body-sm">Overview content</Text> }, { value: "settings", label: "Settings", content: <Text variant="body-sm">Settings content</Text> }]} />
              </Stack>
            )
          }
        ]}
      />
    )
  };
}

function createAngularProductPatternsPage(): DocsPage {
  return {
    id: "product-patterns",
    title: "Product patterns",
    category: "Patterns",
    description: "Composite product patterns are exposed as `bfrs-*` custom elements for Angular templates.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Page header and layout shell",
              code: `<bfrs-page-header title="Your details are ready">
  <bfrs-button slot="actions">Go to Dashboard</bfrs-button>
</bfrs-page-header>

<bfrs-layout-shell>
  <div slot="header">Shiprocket UI</div>
  <bfrs-paper slot="sidebar">Sidebar</bfrs-paper>
  <bfrs-paper>Shell content</bfrs-paper>
</bfrs-layout-shell>`,
              children: (
                <Stack gap={4}>
                  <Paper><Text variant="body-sm" tone="muted">Use PageHeader for title, description, back action, and primary/secondary actions.</Text></Paper>
                  <LayoutShell header={<div className="bfrs-flex bfrs-h-14 bfrs-items-center bfrs-px-5 bfrs-font-semibold"><RocketLaunch className="bfrs-mr-2 bfrs-text-primary" /> Shiprocket UI</div>} sidebar={<Paper><Text variant="body-sm">Sidebar</Text></Paper>} className="bfrs-min-h-[320px] bfrs-rounded-xl bfrs-border bfrs-border-border"><Paper><Text variant="title">Shell content</Text></Paper></LayoutShell>
                </Stack>
              )
            },
            {
              title: "List page layout",
              description: "Full list-page scaffold — project each section through a named slot; unnamed children land in the table region. The layout owns the Shiprocket 4.0 spacing and title typography. Three independent banner slots: banner-above-tabs, banner, and banner-below-filters.",
              code: `<bfrs-list-page-layout title="Orders">
  <bfrs-button slot="actions" size="sm">Add Order</bfrs-button>
  <div slot="kpis">
    <bfrs-kpi-card label="Total orders" value="140"></bfrs-kpi-card>
    <bfrs-kpi-card label="Delivered" value="128" accent="success"></bfrs-kpi-card>
  </div>
  <bfrs-alert slot="banner-above-tabs" tone="warning" title="KYC pending">Upload your GST certificate by Friday.</bfrs-alert>
  <bfrs-tabs slot="tabs" [props]="{ items: tabItems }" [value]="tab" (value-change)="tab = $event.detail.value"></bfrs-tabs>
  <bfrs-alert slot="banner" tone="info" title="Diwali surge">Carrier pickups may be delayed.</bfrs-alert>
  <bfrs-select slot="filters" size="sm" [props]="{ options: statusOptions }" [value]="status" (value-change)="status = $event.detail.value"></bfrs-select>
  <bfrs-tip slot="banner-below-filters">Showing orders from the last 30 days.</bfrs-tip>
  <bfrs-data-table slot="table" [props]="{ data: orders, columns: columns }"></bfrs-data-table>
</bfrs-list-page-layout>`,
              children: <DemoListPageLayout />
            },
            {
              title: "Workspace header",
              code: `<bfrs-workspace-header
  toggle
  [props]="{ search: searchEl, actions: actionsEl }"
  (toggle-sidebar)="collapseSidebar()">
  <span slot="brand">Shiprocket</span>
</bfrs-workspace-header>`,
              children: <DemoWorkspaceHeader />
            },
            {
              title: "Display and summary patterns",
              code: `<bfrs-entity-display-card
  title="Dee-doodles warehouse"
  description="Plot 42, Sector 18, Gurugram, Haryana"
  status="Verified"
  [props]="{ actions: entityActions }"
  (action)="handleAction($event.detail.id)">
</bfrs-entity-display-card>

<bfrs-summary-bar
  total-label="Total"
  total-value="₹900"
  [props]="{ items: summaryItems }">
</bfrs-summary-bar>

<bfrs-collapsible title="Courier rules">Rules and restrictions</bfrs-collapsible>

<bfrs-accordion
  type="single"
  [props]="{ items: sections }"
  (value-change)="openSection = $event.detail.value">
</bfrs-accordion>`,
              children: <DemoDisplayPatterns />
            },
            {
              title: "Chat and loader",
              code: `<bfrs-chat-bubble sender="assistant" eyebrow="Identity" title="Your details are ready">
  We've found your name and email linked to your number.
</bfrs-chat-bubble>

<bfrs-chat-composer
  [value]="message"
  (value-change)="message = $event.detail.value"
  (submit)="sendMessage($event.detail.value)"
></bfrs-chat-composer>

<bfrs-full-page-loader full-screen="false"></bfrs-full-page-loader>`,
              children: (
                <Stack gap={3}>
                  <ChatBubble sender="assistant" eyebrow="Identity" title="Your details are ready">We've found your name and email linked to your number.</ChatBubble>
                  <ChatComposer value="" onValueChange={() => undefined} />
                  <FullPageLoader fullScreen={false} />
                </Stack>
              )
            },
            {
              title: "Business info and progress",
              code: `<bfrs-business-info-display-card
  business-name="Deeksha Gahlot"
  subtitle="deekshagahlot@gmail.com"
  initials="DG"
  [props]="{ sections: businessSections }"
></bfrs-business-info-display-card>

<bfrs-step-progress-card
  current-step="1"
  [props]="{ steps: progressSteps }"
></bfrs-step-progress-card>`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 lg:bfrs-grid-cols-2">
                  <BusinessInfoDisplayCard businessName="Deeksha Gahlot" subtitle="deekshagahlot@gmail.com" initials="DG" sections={businessSections} />
                  <StepProgressCard currentStep={1} steps={progressSteps} />
                </div>
              )
            },
            {
              title: "Horizontal stepper",
              code: `<bfrs-horizontal-stepper
  announcement="Great Choice! Early COD Remittance activation in progress.."
  title="Select a Plan"
  subheading="Get guaranteed remittance from the shipment delivered date."
  current-step="0"
  close-button
  size="sm"
  selectable
  variant="primary"
  [props]="{ steps: remittanceSteps }"
  (close)="dismissStepper()"
  (step-select)="selectStep($event.detail)">
</bfrs-horizontal-stepper>`,
              children: (
                <HorizontalStepper
                  announcement="Great Choice! Early COD Remittance activation in progress.."
                  closeButton
                  onClose={() => undefined}
                  currentStep={0}
                  title="Select a Plan"
                  subheading="Get guaranteed remittance from the shipment delivered date."
                  size="sm"
                  steps={remittanceSteps}
                  variant="primary"
                />
              )
            },
            {
              title: "Reveal and copy",
              code: `<bfrs-reveal-and-copy value="9876543210" allow-copy="true"></bfrs-reveal-and-copy>

<bfrs-reveal-and-copy
  value="ops@deedoodle.com"
  allow-copy="false"
  reveal-label="Show email">
</bfrs-reveal-and-copy>`,
              children: <DemoRevealAndCopy />
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "submit / value-change", description: "ChatComposer emits submit and value-change events for Angular handlers." },
            { name: "reveal-change / copy", description: "RevealAndCopy emits reveal-change when plaintext is shown and copy after successful clipboard writes." },
            { name: "close / step-select", description: "HorizontalStepper emits close from the top-right button and step-select when an enabled step is selected." },
            { name: "variant", description: "HorizontalStepper supports primary, success, info, warning, and neutral visual variants." },
            { name: "[props]", description: "Use property binding for `sections`, `steps`, and other structured pattern props." },
            { name: "class", description: "Use class bindings for width, layout, and instance-specific visual treatment." }
          ]}
        />
      </Stack>
    )
  };
}

const baseDocsPages: DocsPage[] = [
  {
    id: "installation",
    title: "Installation",
    category: "Start",
    description: "Install the public package from npmjs and load the library stylesheet.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
          {
            title: "Install from npmjs",
            description: "The package is public, so consuming apps can install it directly from the npm registry.",
            code: `npm install @bfrs/agentic-components`,
            children: (
              <Stack gap={3}>
                <Text variant="title">Package</Text>
                <Text variant="body-sm" tone="muted">Install `@bfrs/agentic-components` directly from npmjs.</Text>
                <Text variant="body-sm" tone="muted">Install creates `BFRS_AGENTIC_COMPONENTS.md` and adds a reference line to `AGENTS.md` and `CLAUDE.md`.</Text>
              </Stack>
            )
          }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "Repository", description: "https://github.com/bfrs/agentic-components" },
            { name: "Showcase", description: PUBLIC_DOCS_URL },
            { name: "registry", description: "Use the public npmjs registry for install flows." },
            { name: "BFRS_AGENTIC_COMPONENTS.md", description: "Generated component-library knowledge file referenced by agent instruction files." },
            { name: "@bfrs/agentic-components/styles", description: "Import once in the consuming app root so CSS variables, Tailwind layers, and animations are available." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "usage",
    title: "Usage",
    category: "Start",
    description: "Import public components from the package entry and styles from the style export.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
          {
            title: "Basic usage",
            code: `import "@bfrs/agentic-components/styles";
import { Button, Input, Paper, Text } from "@bfrs/agentic-components";

export function Example() {
  return (
    <Paper>
      <Text variant="title">Create shipment</Text>
      <Input placeholder="Pickup pincode" />
      <Button>Continue</Button>
    </Paper>
  );
}`,
            children: (
              <Paper className="bfrs-max-w-sm">
                <Stack gap={3}>
                  <Text variant="title">Create shipment</Text>
                  <Input placeholder="Pickup pincode" />
                  <Button>Continue</Button>
                </Stack>
              </Paper>
            )
          }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "Public imports", description: "Use the package entry for all components. Internal folder paths are intentionally not part of the stable API." },
            { name: "Styles import", description: "Load the stylesheet once. Individual components do not inject their own global CSS." },
            { name: "Tree shaking", description: "The library exports named React components from the package entry for bundler-friendly consumption." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "customization",
    title: "Customization",
    category: "Start",
    description: "Event handlers, refs, class overrides, inline styles, and token overrides follow normal React patterns.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Global theme overrides",
              description: "Use the framework-neutral theme helper at app startup, or scope the generated CSS variables to a subtree.",
              code: `import {
  applyAgenticTheme,
  createAgenticThemeVariables
} from "@bfrs/agentic-components/theme";

applyAgenticTheme({
  colors: {
    primary: "#5B45C2",
    primaryHover: "#4C35AE",
    primarySoft: "#F1ECFB",
    primaryBorder: "#D8CEF9",
    borderFocus: "#5B45C2"
  },
  radius: {
    control: "14px",
    card: "18px"
  },
  shadows: {
    card: "0 12px 32px rgb(91 69 194 / 0.16)",
    elevated: "0 18px 48px rgb(91 69 194 / 0.22)"
  }
});

const scopedTheme = createAgenticThemeVariables({
  colors: { primary: "#5B45C2", primarySoft: "#F1ECFB" },
  radius: { control: "10px" }
});

<div style={scopedTheme}>
  <Button>Themed button</Button>
</div>`,
              children: <DemoThemeOverrides />
            },
            {
              title: "Events and style overrides",
              description: "Most components forward native props. BFRS-prefixed className values are merged with tailwind-merge while unprefixed host classes pass through.",
              code: `<Button
  onClick={() => save()}
  onMouseEnter={() => prefetch()}
  className="bfrs-bg-accent hover:bfrs-bg-accent/90"
>
  Save
</Button>

<Paper
  onClick={() => openDetails()}
  className="bfrs-cursor-pointer bfrs-rounded-2xl bfrs-border-border-strong bfrs-shadow-elevated"
>
  Clickable card
</Paper>

<div style={{ "--bfrs-color-primary": "251 51% 52%" }}>
  <Button>Token override</Button>
</div>`,
              children: (
                <Stack gap={4}>
                  <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-3">
                    <Button onClick={() => undefined} onMouseEnter={() => undefined} className="bfrs-bg-accent hover:bfrs-bg-accent/90">Save</Button>
                    <Paper onClick={() => undefined} className="bfrs-cursor-pointer bfrs-rounded-2xl bfrs-border-border-strong bfrs-shadow-elevated">Clickable card</Paper>
                  </div>
                  <Text variant="body-sm" tone="muted">
                    Native props like onClick, onKeyDown, aria-*, data-*, id, className, style, and ref are supported where the underlying component owns a compatible DOM element.
                  </Text>
                </Stack>
              )
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "@bfrs/agentic-components/theme", description: "Framework-neutral subpath for theme override helpers. It does not require importing the React component entry." },
            { name: "applyAgenticTheme", description: "Applies color, radius, shadow, gradient, spacing, typography, motion, and raw CSS variable overrides to an explicit target or the first mounted BFRS scope." },
            { name: "createAgenticThemeVariables", description: "Returns a CSS variable object for scoped overrides through React `style` or manual DOM application." },
            { name: "className", description: "Preferred per-instance styling hook. Conflicting Tailwind utilities are merged through cn()/tailwind-merge." },
            { name: "style", description: "Use for dynamic values or CSS variable overrides. Prefer semantic props and tokens for normal styling." },
            { name: "onClick / onKeyDown / onBlur", description: "Native handlers pass through on components backed by standard DOM elements." },
            { name: "onValueChange / onOpenChange", description: "Radix-backed and controlled components expose explicit callbacks for state changes." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "layout-primitives",
    title: "Layout primitives",
    category: "Foundations",
    description: "Box, Container, Stack, Grid, Paper, Text, and Icon.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
          {
            title: "Paper variants",
            code: `<Grid columns={4}>
  <Paper variant="default">Default</Paper>
  <Paper variant="muted">Muted</Paper>
  <Paper variant="elevated">Elevated</Paper>
  <Paper variant="outlined">Outlined</Paper>
  <Paper variant="glass">Glass</Paper>
</Grid>`,
            children: (
              <Grid columns={4}>
                {(["default", "muted", "elevated", "outlined", "glass"] as const).map((variant) => (
                  <Paper key={variant} variant={variant}><Text variant="body-sm">{variant}</Text></Paper>
                ))}
              </Grid>
            )
          },
          {
            title: "Typography variants",
            code: `<Text variant="display">Display</Text>
<Text variant="h1">Heading 1</Text>
<Text variant="h2">Heading 2</Text>
<Text variant="h3">Heading 3</Text>
<Text variant="title">Title</Text>
<Text variant="body">Body</Text>
<Text variant="body-sm">Body small</Text>
<Text variant="caption">Caption</Text>
<Text variant="label" tone="accent">Label</Text>`,
            children: (
              <Stack gap={3}>
                <Text variant="display">Display</Text>
                <Text variant="h1">Heading 1</Text>
                <Text variant="h2">Heading 2</Text>
                <Text variant="h3">Heading 3</Text>
                <Text variant="title">Title</Text>
                <Text variant="body">Body text</Text>
                <Text variant="body-sm" tone="muted">Body small muted</Text>
                <Text variant="caption" tone="secondary">Caption</Text>
                <Text variant="label" tone="accent">Account Summary</Text>
              </Stack>
            )
          },
          {
            title: "Layout helpers",
            code: `<Container size="md">
  <Stack gap={4}>
    <Box className="bfrs-rounded-lg bfrs-bg-surface-muted bfrs-p-4">Box</Box>
    <Grid columns={3}>
      <Paper>One</Paper>
      <Paper>Two</Paper>
      <Paper>Three</Paper>
    </Grid>
  </Stack>
</Container>`,
            children: (
              <Container size="md" className="bfrs-px-0">
                <Stack gap={4}>
                  <Box className="bfrs-rounded-lg bfrs-bg-surface-muted bfrs-p-4">Box</Box>
                  <Grid columns={3}><Paper>One</Paper><Paper>Two</Paper><Paper>Three</Paper></Grid>
                  <Icon icon={RocketLaunch} className="bfrs-text-primary" />
                </Stack>
              </Container>
            )
          }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "as / asChild", description: "Layout primitives keep DOM output predictable. Use Box for polymorphic layout and semantic elements where needed." },
            { name: "className", description: "Override spacing, width, color, and layout utilities per instance while keeping token-backed defaults." },
            { name: "onClick / aria-*", description: "Paper and Box accept compatible DOM props, but interactive surfaces still need accessible roles and keyboard handling." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "colors",
    title: "Colors",
    category: "Foundations",
    description: "Neutral ramp and semantic color tokens. Override them through the theme helper or CSS variables.",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Palette",
              description: "Component surfaces, text, and borders all derive from these tokens. Override a BFRS scope with applyAgenticTheme or raw --bfrs-color-* variables.",
              code: `// Tokens resolve through hsl(var(--bfrs-color-*))
<div className="bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-text-text-primary" />
<div className="bfrs-bg-primary bfrs-text-primary-contrast" />

// Override the mounted BFRS scope
applyAgenticTheme({
  colors: {
    surfaceApp: "#F6F9F6",
    border: "#E5EBEA",
    textPrimary: "#29343D"
  }
});`,
              children: <DemoColorPalette />
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "Neutral ramp", description: "Six-step grayscale from ink (#29343D) to white (#FFFFFF) backing every surface, border, and text token." },
            { name: "hsl(var(--bfrs-color-*))", description: "Semantic tokens are stored as HSL channels and consumed through hsl(var(--bfrs-color-*)) so Tailwind alpha utilities keep working." },
            { name: "applyAgenticTheme / CSS variables", description: "Override any token globally with the theme helper or by setting --bfrs-color-* variables on a scope." },
            { name: "Soft / contrast variants", description: "Status colors expose -soft backgrounds and -contrast foregrounds for accessible badges and alerts." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "buttons",
    title: "Button",
    category: "Actions",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
          {
            title: "Button variants and sizes",
            code: `<Button>Primary</Button>
<Button variant="secondary">Secondary</Button>
<Button variant="outline">Outline</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="destructive">Destructive</Button>
<Button variant="link">Link</Button>

<Button size="sm">Small</Button>
<Button size="md">Medium</Button>
<Button size="lg">Large</Button>
<Button loading>Loading</Button>
<Button disabled>Disabled</Button>

<Button
  onClick={() => submit()}
  className="bfrs-bg-accent hover:bfrs-bg-accent/90"
>
  Custom action
</Button>`,
            children: (
              <Stack gap={4}>
                <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-3">
                  <Button leftIcon={<Plus size={16} />}>Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="destructive">Destructive</Button>
                  <Button variant="link">Link</Button>
                </div>
                <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-3">
                  <Button size="sm">Small</Button>
                  <Button size="md">Medium</Button>
                  <Button size="lg">Large</Button>
                  <Button loading>Loading</Button>
                  <Button disabled>Disabled</Button>
                  <IconButton aria-label="More actions" icon={<DotsThree size={22} weight="bold" />} variant="outline" />
                  <Button onClick={() => undefined} className="bfrs-bg-accent hover:bfrs-bg-accent/90">Custom action</Button>
                </div>
              </Stack>
            )
          }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "onClick", description: "Runs on activation and receives the native button click event." },
            { name: "onMouseEnter / onFocus / data-*", description: "Native button attributes and event listeners pass through to the underlying button." },
            { name: "loading", description: "Disables activation, sets busy semantics, and renders the spinner without changing the public click API." },
            { name: "className", description: "Use token utilities or project utilities to override visuals. Conflicting Tailwind classes are merged." },
            { name: "aria-label", description: "Required on IconButton because the visual icon is not enough for assistive technology." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "forms",
    title: "Forms",
    category: "Inputs",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
          {
            title: "Text inputs",
            code: `<FormField label="Email" helperText="Used for notifications.">
  <Input
    placeholder="deeksha@example.com"
    leftIcon={<Envelope size={18} />}
    onChange={(event) => setEmail(event.target.value)}
    onBlur={() => validateEmail()}
  />
</FormField>

<FormField label="Company" errorText="Company name is required.">
  <Input error placeholder="Nykaa Fashion" />
</FormField>

<FormField label="Pickup pincode">
  <Input type="number" placeholder="122099" inputMode="numeric" />
</FormField>

<FormField label="Search">
  <Input disabled placeholder="Disabled input" leftIcon={<MagnifyingGlass size={18} />} />
</FormField>

<Textarea placeholder="Add internal notes..." />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <FormField label="Email" helperText="Used for notifications."><Input placeholder="deeksha@example.com" leftIcon={<Envelope size={18} />} onChange={() => undefined} onBlur={() => undefined} /></FormField>
                <FormField label="Company" errorText="Company name is required."><Input error placeholder="Nykaa Fashion" /></FormField>
                <FormField label="Pickup pincode"><Input type="number" placeholder="122099" inputMode="numeric" /></FormField>
                <FormField label="Search"><Input disabled placeholder="Disabled input" leftIcon={<MagnifyingGlass size={18} />} /></FormField>
                <FormField label="Notes"><Textarea placeholder="Add internal notes..." /></FormField>
                <Label required>Standalone label</Label>
              </div>
            )
          },
          {
            title: "Input — sizes",
            description: "Input supports size=\"md\" (36px, default) and size=\"sm\" (28px). Font is 12px in both sizes. Use sm for compact toolbar or table-filter contexts.",
            code: `// md — 36px (default)
<Input placeholder="Full name" />
<Input placeholder="With value" defaultValue="Deeksha Gahlot" />
<Input error placeholder="Error state" />
<Input prefix="₹" placeholder="0.00" />

// sm — 28px
<Input size="sm" placeholder="Full name" />
<Input size="sm" defaultValue="Deeksha Gahlot" />
<Input size="sm" error placeholder="Error state" />
<Input size="sm" prefix="₹" placeholder="0.00" />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <Stack gap={2}>
                  <Text variant="label" tone="muted">md — 36px (default)</Text>
                  <Input placeholder="Full name" />
                  <Input defaultValue="Deeksha Gahlot" />
                  <Input error placeholder="Error state" />
                  <Input prefix="₹" placeholder="0.00" />
                </Stack>
                <Stack gap={2}>
                  <Text variant="label" tone="muted">sm — 28px</Text>
                  <Input size="sm" placeholder="Full name" />
                  <Input size="sm" defaultValue="Deeksha Gahlot" />
                  <Input size="sm" error placeholder="Error state" />
                  <Input size="sm" prefix="₹" placeholder="0.00" />
                </Stack>
              </div>
            )
          },
          {
            title: "Validated form with API checks",
            description: "Show client validation, simulated API validation, field-level errors below inputs, and toast feedback.",
            code: `type FieldValues = {
  businessName: string;
  email: string;
  pickupPincode: string;
};

type FieldErrors = Partial<Record<keyof FieldValues, string>>;

function validateClient(values: FieldValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.businessName.trim()) errors.businessName = "Business name is required.";
  if (!values.email.trim()) errors.email = "Email is required.";
  else if (!/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(values.email)) errors.email = "Enter a valid email address.";
  if (!/^\\d{6}$/.test(values.pickupPincode)) errors.pickupPincode = "Enter a 6-digit pickup pincode.";
  return errors;
}

function ShipmentValidationForm() {
  const { success, danger } = useToast();
  const [values, setValues] = useState<FieldValues>({
    businessName: "Dee-doodles",
    email: "ops@deedoodles.com",
    pickupPincode: "000000"
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validateClient(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      danger({ title: "Validation failed", description: "Fix the highlighted fields before submitting." });
      return;
    }

    setSubmitting(true);
    const pincodeError = await validatePickupPincode(values.pickupPincode);
    setSubmitting(false);

    if (pincodeError) {
      setErrors({ pickupPincode: pincodeError });
      danger({ title: "API validation failed", description: "The serviceability check returned a field error." });
      return;
    }

    setErrors({});
    success({ title: "Form validated", description: "The shipment form is ready to submit." });
  }

  return (
    <form onSubmit={submit} className="bfrs-grid bfrs-gap-4">
      <FormField label="Business name" required errorText={errors.businessName}>
        <Input
          error={Boolean(errors.businessName)}
          value={values.businessName}
          onChange={(event) => setValues({ ...values, businessName: event.currentTarget.value })}
        />
      </FormField>

      <FormField label="Email" required errorText={errors.email} helperText="Used for shipment notifications.">
        <Input
          error={Boolean(errors.email)}
          type="email"
          value={values.email}
          onChange={(event) => setValues({ ...values, email: event.currentTarget.value })}
        />
      </FormField>

      <FormField label="Pickup pincode" required errorText={errors.pickupPincode} helperText="Serviceability is checked by the API on submit.">
        <Input
          error={Boolean(errors.pickupPincode)}
          inputMode="numeric"
          suffix="IN"
          value={values.pickupPincode}
          onChange={(event) => setValues({ ...values, pickupPincode: event.currentTarget.value })}
        />
      </FormField>

      <Button type="submit" loading={submitting}>Validate form</Button>
    </form>
  );
}

async function validatePickupPincode(value: string) {
  await new Promise((resolve) => window.setTimeout(resolve, 500));
  return value === "000000" ? "Pickup is not serviceable for this pincode." : undefined;
}

<ToastProvider>
  <ShipmentValidationForm />
</ToastProvider>`,
            children: <DemoValidatedForm />
          },
          {
            title: "Selection controls",
            code: `<Select
  options={options}
  placeholder="Choose business type"
  onValueChange={(value) => setBusinessType(value)}
/>
<SearchableSelect
  options={options}
  placeholder="Search categories"
  onValueChange={(value) => setCategory(value)}
/>
<MultiSelect
  options={options}
  value={channels}
  placeholder="Select channels"
  onValueChange={setChannels}
/>
<Checkbox
  label="Accept terms"
  description="Use for boolean or multi-select choices."
  defaultChecked
/>
<Radio
  options={[
    { value: "registered", label: "Registered business", description: "Company GST and business KYC are available." },
    { value: "individual", label: "Individual seller", description: "Use personal details for seller verification." }
  ]}
  defaultValue="registered"
/>
<Switch label="Enable notifications" defaultChecked />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <Select options={[{ value: "registered", label: "Registered business" }, { value: "individual", label: "Individual seller" }]} placeholder="Choose business type" onValueChange={() => undefined} />
                <SearchableSelect options={[{ value: "registered", label: "Registered business" }, { value: "individual", label: "Individual seller" }]} placeholder="Search categories" onValueChange={() => undefined} />
                <MultiSelect options={[{ value: "registered", label: "Registered business" }, { value: "individual", label: "Individual seller" }, { value: "marketplace", label: "Marketplace seller" }]} defaultValue={["registered", "marketplace"]} placeholder="Select channels" onValueChange={() => undefined} />
                <Stack gap={3}>
                  <Checkbox label="Accept terms" description="Use for boolean or multi-select choices." defaultChecked />
                  <Checkbox label="Partially selected" description="Parent row with mixed child selections." checked="indeterminate" />
                  <Switch label="Enable notifications" defaultChecked />
                </Stack>
                <Radio options={businessRadioOptions} defaultValue="registered" />
              </div>
            )
          },
          {
            title: "Selection controls — sizes",
            description: "Select, SearchableSelect, MultiSelect, and DateRangePicker all support size=\"md\" (36px, default) and size=\"sm\" (28px). Font is 12px in both sizes.",
            code: `// md — 36px (default)
<Select options={options} placeholder="Select business type" />
<SearchableSelect options={options} placeholder="Search categories" />
<MultiSelect options={options} placeholder="Select channels" />
<DateRangePicker value={range} onChange={setRange} />

// sm — 28px
<Select size="sm" options={options} placeholder="Select business type" />
<SearchableSelect size="sm" options={options} placeholder="Search categories" />
<MultiSelect size="sm" options={options} placeholder="Select channels" />
<DateRangePicker size="sm" value={range} onChange={setRange} />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <Stack gap={2}>
                  <Text variant="label" tone="muted">md — 36px (default)</Text>
                  <Select options={businessSelectOptions} placeholder="Select business type" />
                  <SearchableSelect options={businessSelectOptions} placeholder="Search categories" onValueChange={() => undefined} />
                  <MultiSelect options={channelSelectOptions} placeholder="Select channels" onValueChange={() => undefined} />
                  <DateRangePicker placeholder="Select date range" onChange={() => undefined} />
                </Stack>
                <Stack gap={2}>
                  <Text variant="label" tone="muted">sm — 28px</Text>
                  <Select size="sm" options={businessSelectOptions} placeholder="Select business type" />
                  <SearchableSelect size="sm" options={businessSelectOptions} placeholder="Search categories" onValueChange={() => undefined} />
                  <MultiSelect size="sm" options={channelSelectOptions} placeholder="Select channels" onValueChange={() => undefined} />
                  <DateRangePicker size="sm" placeholder="Select date range" onChange={() => undefined} />
                </Stack>
              </div>
            )
          },
          {
            title: "Select & SearchableSelect — grouping & menu width",
            description: "Options can be grouped under headings, and the menu can match the trigger or grow wider (menuWidth: \"trigger\" | \"auto\" | number).",
            code: `<Select options={options} />

// grouped options (add a \`group\` to each option)
<Select
  options={[
    { value: "cod", label: "Cash on Delivery", group: "Payment" },
    { value: "prepaid", label: "Prepaid", group: "Payment" },
    { value: "dom", label: "Domestic", group: "Shipping" }
  ]}
  menuWidth="auto"
/>
<Select options={options} menuWidth={320} />          // fixed-width menu
<SearchableSelect menuWidth="auto" options={groupedOptions} />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <Stack gap={2}>
                  <Select options={businessSelectOptions} placeholder="Select business type" />
                </Stack>
                <Stack gap={2}>
                  <Select options={groupedSelectOptions} placeholder="Grouped options" />
                  <Select options={businessSelectOptions} menuWidth={320} placeholder="menuWidth: 320px" />
                  <SearchableSelect menuWidth="auto" options={groupedSelectOptions} placeholder="Searchable + grouped" searchPlaceholder="Search…" onValueChange={() => undefined} />
                </Stack>
              </div>
            )
          },
          {
            title: "MultiSelect — filter variant, apply mode & footer button variants",
            description: "MultiSelect uses the same trigger height as Select (36px md, 28px sm). variant=\"filter\" removes the search box for filter dropdowns. Grouped options, wider menus, deferred apply, footer variants, and custom footers are supported.",
            code: `// deferred commit: onValueChange fires ONLY on Apply
<MultiSelect
  options={channelOptions}
  commitMode="apply"
  applyVariant="primary"   // footer Apply button variant
  cancelVariant="secondary" // footer Cancel button variant
  applyLabel="Apply"
  cancelLabel="Cancel"
  menuWidth="auto"
  value={channels}
  onValueChange={setChannels}
  onApply={setChannels}
/>

// filter variant: no search input
<MultiSelect
  options={channelOptions}
  variant="filter"
  placeholder="Filter without search"
  onValueChange={setChannels}
/>

// fully custom footer
<MultiSelect options={options} footer={<MyFooterActions />} />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <Stack gap={2}>
                  <MultiSelect options={channelSelectOptions} defaultValue={["amazon", "shopify"]} placeholder="Select channels" onValueChange={() => undefined} />
                  <MultiSelect options={groupedSelectOptions} menuWidth="auto" defaultValue={["cod", "dom"]} placeholder="Grouped · wide menu" onValueChange={() => undefined} />
                  <MultiSelect options={channelSelectOptions} variant="filter" placeholder="Filter without search" onValueChange={() => undefined} />
                </Stack>
                <Stack gap={2}>
                  <DemoMultiSelectApply />
                  <MultiSelect options={channelSelectOptions} commitMode="apply" applyVariant="outline" cancelVariant="ghost" placeholder="apply · outline / ghost" onValueChange={() => undefined} />
                  <MultiSelect options={channelSelectOptions} commitMode="apply" applyVariant="secondary" cancelVariant="outline" applyLabel="Save" cancelLabel="Cancel" placeholder="apply · secondary / outline" onValueChange={() => undefined} />
                  <MultiSelect
                    options={channelSelectOptions}
                    placeholder="Custom footer"
                    onValueChange={() => undefined}
                    footer={
                      <div className="bfrs-flex bfrs-items-center bfrs-justify-between bfrs-px-1">
                        <Button variant="link" size="xs">Saved filters</Button>
                        <Button variant="primary" size="xs">Done</Button>
                      </div>
                    }
                  />
                </Stack>
              </div>
            )
          },
          {
            title: "MultiSelect — stable width in unconstrained containers",
            description: "When the field (or its parent) has no width, the trigger holds a min-width floor and stays put as options are selected — extra selections truncate into the +N pill instead of widening the field. Both wrappers below are shrink-to-fit (inline-flex) with no width set; the empty field and the one with four selections render at the same width. Give the field an explicit width, or wrap it in a wider/constrained container, when you want it larger.",
            code: `// No width on the field or its parent — the trigger no longer grows per pill.
// contain: inline-size + a min-width floor keep selections from widening it.
<div style={{ display: "inline-flex" }}>
  <MultiSelect
    options={channelOptions}
    defaultValue={["amazon", "flipkart", "shopify", "woocommerce"]}
    placeholder="Select channels"
  />
</div>`,
            children: (
              <Stack gap={1}>
                <Text variant="caption" tone="muted">Shrink-to-fit parents (inline-flex, no width) · empty vs. four selected — same width</Text>
                <div className="bfrs-flex bfrs-flex-wrap bfrs-items-start bfrs-gap-4">
                  <div style={{ display: "inline-flex", padding: 4, border: "1px dashed hsl(var(--bfrs-color-border))", borderRadius: 8 }}>
                    <MultiSelect options={channelSelectOptions} placeholder="Select channels" onValueChange={() => undefined} />
                  </div>
                  <div style={{ display: "inline-flex", padding: 4, border: "1px dashed hsl(var(--bfrs-color-border))", borderRadius: 8 }}>
                    <MultiSelect
                      options={channelSelectOptions}
                      defaultValue={["amazon", "flipkart", "shopify", "woocommerce"]}
                      placeholder="Select channels"
                      onValueChange={() => undefined}
                    />
                  </div>
                </div>
              </Stack>
            )
          },
          {
            title: "TagsInput / TagInput — chip entry",
            description: "Controlled chip input for free-form order tags with optional existing-tag suggestions. It is creatable by default, unlike MultiSelect.",
            code: `const [tags, setTags] = useState<string[]>([]);

<TagsInput
  value={tags}
  onChange={setTags}
  suggestions={["urgent", "fragile", "cod", "vip"]}
  creatable
  placeholder="Add Tag"
/>

// react-hook-form
<Controller
  name="order_tag"
  control={control}
  render={({ field }) => (
    <TagsInput
      value={field.value ?? []}
      onChange={field.onChange}
      suggestions={allTags}
      creatable
      placeholder="Add Tag"
    />
  )}
/>`,
            children: <DemoTagInput />
          },
          {
            title: "Checkbox, Radio & Switch — sizes & states",
            description: "Boolean and single-choice controls with their sizes and states.",
            code: `<Checkbox size="sm" label="sm" defaultChecked />
<Checkbox size="md" label="md" defaultChecked />
<Checkbox size="lg" label="lg" defaultChecked />
<Checkbox label="Indeterminate" checked="indeterminate" />
<Checkbox label="Disabled" disabled />

<Radio
  options={[
    { value: "registered", label: "Registered business", description: "Company GST and business KYC are available." },
    { value: "individual", label: "Individual seller", description: "Use personal details for seller verification." }
  ]}
  defaultValue="registered"
/>
<Switch label="Enable notifications" defaultChecked />`,
            children: (
              <div className="bfrs-grid bfrs-gap-5 md:bfrs-grid-cols-2">
                <Stack gap={2}>
                  <div className="bfrs-flex bfrs-items-center bfrs-gap-4">
                    <Checkbox size="sm" label="sm" defaultChecked />
                    <Checkbox size="md" label="md" defaultChecked />
                    <Checkbox size="lg" label="lg" defaultChecked />
                  </div>
                  <Checkbox label="Partially selected" checked="indeterminate" />
                  <Checkbox label="Disabled" disabled />
                </Stack>
                <Stack gap={2}>
                  <Radio options={businessRadioOptions} defaultValue="registered" />
                  <Switch label="Enable notifications" defaultChecked />
                  <Switch label="Auto-assign couriers" />
                  <Switch label="Disabled" disabled />
                </Stack>
              </div>
            )
          },
          {
            title: "Range and color controls",
            description: "Use these instead of raw range inputs, color inputs, or palette chip buttons in consuming apps.",
            code: `const paletteOptions = [
  "#5B45C2",
  "#2563EB",
  "#059669",
  ["#5B45C2", "#2563EB", "#059669"]
];

<Slider
  label="Confidence threshold"
  value={confidence}
  min={0}
  max={100}
  step={5}
  formatValue={(value) => \`\${value}%\`}
  onValueChange={setConfidence}
/>

<ColorPicker
  label="Brand color"
  value={brandColor}
  onValueChange={setBrandColor}
/>

<ColorSwatchGroup
  label="Palette"
  value={palette}
  options={paletteOptions}
  onValueChange={setPalette}
/>`,
            children: <DemoRangeAndColorControls />
          },
          {
            title: "Additional form controls",
            description: "Reusable controls for card radios, segmented chips, tag chips, quantities, free-text suggestions, uploads, and field-level reveal.",
            code: `<OptionCardGroup
  value={fulfillmentMode}
  onValueChange={setFulfillmentMode}
  options={[
    { value: "standard", label: "Standard", description: "Lowest cost option." },
    { value: "express", label: "Express", description: "Prioritize faster pickup." }
  ]}
/>

<SelectableChipGroup multiple value={channels} options={channelOptions} addAction={{ label: "Add" }} />
<NumberStepper value={quantity} min={1} max={10} onValueChange={setQuantity} />
<SuggestInput value={city} suggestions={citySuggestions} onValueChange={setCity} />
<TagsInput value={orderTags} onChange={setOrderTags} suggestions={allTags} creatable placeholder="Add Tag" />
<FileDropzone title="Upload invoice" onFilesChange={setFiles} />
<RevealField label="Masked account field" value="9876543210" allowCopy />

<bfrs-option-card-group [props]="{ options: fulfillmentOptions, value: fulfillmentMode }" (value-change)="fulfillmentMode = $event.detail.value"></bfrs-option-card-group>
<bfrs-selectable-chip-group multiple [props]="{ options: channelOptions, value: channels }" (value-change)="channels = $event.detail.value"></bfrs-selectable-chip-group>
<bfrs-number-stepper [value]="quantity" min="1" max="10" (value-change)="quantity = $event.detail.value"></bfrs-number-stepper>
<bfrs-suggest-input [props]="{ suggestions: citySuggestions, value: city }" (value-change)="city = $event.detail.value"></bfrs-suggest-input>
<bfrs-tag-input [props]="{ values: orderTags, suggestions: allTags, creatable: true }" (value-change)="orderTags = $event.detail.values"></bfrs-tag-input>
<bfrs-file-dropzone title="Upload invoice" (files-change)="files = $event.detail.files"></bfrs-file-dropzone>
<bfrs-reveal-field label="Masked account field" value="9876543210"></bfrs-reveal-field>`,
            children: <DemoAdditionalFormControls />
          },
          {
            title: "Date range picker",
            code: `const initialRange = presetToRange("Last 30 days");

const [range, setRange] = useState<DateRangeValue>({
  preset: "Last 30 days",
  ...initialRange
});

<DateRangePicker
  value={range}
  onChange={setRange}
  maxRangeDays={365}
/>`,
            children: <DemoDateRangePicker />
          },
          {
            title: "Date range picker above a horizontally scrollable table",
            description: "Verify the picker popover renders above the table's overflow container — it should not be clipped. Open the picker, then scroll the table sideways.",
            code: `<Stack gap={3}>
  <DateRangePicker value={range} onChange={setRange} maxRangeDays={365} />
  <DataTable
    columns={columns}
    data={rows}
    getRowId={(row) => String(row.id)}
    sorting={sorting}
    onSortingChange={setSorting}
    minWidth={1600}
  />
</Stack>`,
            children: <DemoDateRangePickerWithTable />
          }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "onChange / onBlur", description: "Input and Textarea forward native form events, so form libraries can register them normally." },
            { name: "onValueChange", description: "Select, SearchableSelect, MultiSelect, Slider, ColorPicker, ColorSwatchGroup, Radio, and Switch expose value callbacks for controlled state." },
            { name: "MultiSelect filter variant", description: "Use variant=\"filter\" to remove the popover search box in filter dropdowns, or searchable={false} when only the search behavior should change." },
            { name: "DateRangePicker", description: "Preset ranges commit immediately; custom ranges use draft state and Apply/Cancel controls." },
            { name: "Checkbox vs Radio", description: "Use Checkbox for independent true/false or multi-select choices. Use Radio when exactly one option in a set can be selected." },
            { name: "Radio option descriptions", description: "Radio options accept description for secondary text below the primary label, matching Checkbox's label + description stack." },
            { name: "className", description: "Override field width, spacing, borders, and state colors per instance." },
            { name: "aria-describedby / aria-invalid", description: "FormField connects helper and error text to controls for accessible validation." },
            { name: "ref", description: "Input-like components forward refs to the focusable control where applicable." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "feedback",
    title: "Feedback",
    category: "Feedback",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
          {
            title: "Status components",
            code: `<Chip tone="primary">Verified</Chip>
<Chip tone="success" variant="outline">Live</Chip>
<Badge tone="info">New</Badge>
<Alert tone="info" title="Info">A neutral informational state.</Alert>
<Alert tone="success" title="Success">Details fetched successfully.</Alert>
<Alert tone="warning" title="Warning">Review required.</Alert>
<Alert tone="danger" title="Payment failed" message="Update payment details and retry." />
<Alert tone="warning" title="Review required" />`,
            children: (
              <Stack gap={4}>
                <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-2">
                  <Chip tone="neutral">Neutral</Chip><Chip tone="primary">Primary</Chip><Chip tone="success" variant="outline">Success</Chip><Chip tone="warning">Warning</Chip><Chip tone="danger" onRemove={() => undefined}>Danger</Chip>
                  <Badge>Neutral</Badge><Badge tone="primary">Primary</Badge><Badge tone="success">Success</Badge><Badge tone="warning">Warning</Badge><Badge tone="danger">Danger</Badge><Badge tone="info">Info</Badge>
                </div>
                <div className="bfrs-grid bfrs-gap-3 md:bfrs-grid-cols-2">
                  <Alert tone="info" title="Info">A neutral informational state.</Alert>
                  <Alert tone="success" title="Success">Details fetched successfully.</Alert>
                  <Alert tone="warning" title="Warning">Review required.</Alert>
                  <Alert tone="danger" title="Payment failed" message="Update payment details and retry." />
                  <Alert tone="warning" title="Review required" />
                  <Alert tone="info"><strong>Order #SR12345</strong> has been picked up and is on the way.</Alert>
                </div>
              </Stack>
            )
          },
          {
            title: "Alert sizes",
            description: "Alert supports sm, md (default), and lg sizes — controls padding, text, and icon scale.",
            code: `<Alert size="xs" tone="info" title="XSmall">Ultra-compact inline alert.</Alert>
<Alert size="sm" tone="info" title="Small">Compact alert for dense layouts.</Alert>
<Alert size="md" tone="info" title="Medium">Default alert size.</Alert>
<Alert size="lg" tone="info" title="Large">Spacious alert for prominent messages.</Alert>`,
            children: (
              <Stack gap={3}>
                <Alert size="xs" tone="info" title="XSmall">Ultra-compact inline alert.</Alert>
                <Alert size="sm" tone="success" title="Small">Compact alert for dense layouts.</Alert>
                <Alert size="md" tone="warning" title="Medium">Default alert size.</Alert>
                <Alert size="lg" tone="info" title="Large">Spacious alert for prominent messages.</Alert>
              </Stack>
            )
          },
          {
            title: "Sizes",
            description: "Chip and Badge both support four sizes: xs, sm (default), md, and lg.",
            code: `<Chip size="xs" tone="primary">xs</Chip>
<Chip size="sm" tone="primary">sm</Chip>
<Chip size="md" tone="primary">md</Chip>
<Chip size="lg" tone="primary">lg</Chip>
<Badge size="xs" tone="success">xs</Badge>
<Badge size="sm" tone="success">sm</Badge>
<Badge size="md" tone="success">md</Badge>
<Badge size="lg" tone="success">lg</Badge>`,
            children: (
              <Stack gap={3}>
                <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-2">
                  <Chip size="xs" tone="primary">xs</Chip>
                  <Chip size="sm" tone="primary">sm</Chip>
                  <Chip size="md" tone="primary">md</Chip>
                  <Chip size="lg" tone="primary">lg</Chip>
                </div>
                <div className="bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-gap-2">
                  <Badge size="xs" tone="success">xs</Badge>
                  <Badge size="sm" tone="success">sm</Badge>
                  <Badge size="md" tone="success">md</Badge>
                  <Badge size="lg" tone="success">lg</Badge>
                </div>
              </Stack>
            )
          },
          {
            title: "Loading and empty states",
            code: `<Skeleton variant="text" />
<Skeleton variant="text" animated={false} />
<Skeleton variant="card" />
<Spinner />
<EmptyState title="No notifications yet" />`,
            children: (
              <Stack gap={4}>
                <div className="bfrs-grid bfrs-gap-3 md:bfrs-grid-cols-4"><Skeleton variant="text" /><Skeleton variant="block" /><Skeleton variant="card" /><Skeleton variant="avatar" animated={false} /></div>
                <Spinner />
                <EmptyState icon={<Bell size={24} />} title="No notifications yet" description="Important updates will appear here." />
              </Stack>
            )
          },
          {
            title: "Progress bars",
            description: "Determinate progress with an optional value label, tones, and sizes, plus an indeterminate track for unknown durations.",
            code: `<ProgressBar value={64} label="Upload progress" showValue />
<ProgressBar value={82} tone="success" size="lg" />
<ProgressBar indeterminate label="Syncing changes…" />`,
            children: <DemoProgressBar />
          },
          {
            title: "Toast manager",
            code: `<ToastProvider>
  <SaveButton />
</ToastProvider>

function SaveButton() {
  const { success, danger } = useToast();
  return <Button onClick={() => success("Action completed")}>Save</Button>;
}`,
            children: <DemoToastManager />
          },
          {
            title: "Callouts",
            code: `<InfoCard
  tone="info"
  title="Courier allocation is ready"
  description="Review serviceability before assigning the shipment."
/>

<Tip>Recommended</Tip>
<Tip tone="success">Verified</Tip>

<bfrs-info-card title="Courier allocation is ready" description="Review serviceability before assigning the shipment."></bfrs-info-card>
<bfrs-tip>Recommended</bfrs-tip>`,
            children: <DemoCalloutPatterns />
          }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "ProgressBar value / max", description: "Determinate fill is value/max clamped to 0–100%. Set showValue to render the percentage, or pass formatValue for custom text." },
            { name: "ProgressBar indeterminate", description: "Renders an animated sliding track for unknown-duration work; drops aria-valuenow so assistive tech announces a busy state." },
            { name: "onRemove", description: "Chip calls this when the removable close action is activated." },
            { name: "Alert variants", description: "`tone=\"info\" | \"success\" | \"warning\" | \"danger\"` controls alert color, border, and icon. Warning uses the shared 11px triangle icon with `#D97706`, `rgba(217, 119, 6, 0.08)` background, and `rgba(217, 119, 6, 0.2)` border." },
            { name: "Alert content", description: "Set title and children independently, or pass message/description for the supporting text. Omit supporting text for a single-line alert." },
            { name: "action", description: "Alert and EmptyState accept action slots for contextual recovery or next steps." },
            { name: "ToastProvider / useToast", description: "Wrap the app once, then call toast helpers from app event handlers. Toast state stays outside route and API logic." },
            { name: "className", description: "Override status spacing, sizing, or surfaces while keeping semantic tone props." },
            { name: "aria-*", description: "Forward accessible labels or live-region attributes when a status is announced dynamically." }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "overlays",
    title: "Overlays",
    category: "Overlays",
    content: (
      <ComponentPage
        examples={[
          {
            title: "Modal, drawer, dropdown, tooltip, popover",
            code: `<Modal title="Confirm" trigger={<Button>Open modal</Button>}>...</Modal>
<Drawer title="Filters" trigger={<Button>Open drawer</Button>}>...</Drawer>
<Dropdown trigger={<IconButton ... />} items={items} />
<Tooltip content="Helpful text"><Button>Hover</Button></Tooltip>
<Tooltip content="White tooltip" variant="white"><Button variant="outline">Hover</Button></Tooltip>
<Popover trigger={<Button>Open</Button>}>...</Popover>`,
            children: (
              <div className="bfrs-flex bfrs-flex-wrap bfrs-gap-3">
                <Modal title="Confirm account details" description="Modal uses focus trapping." trigger={<Button>Open modal</Button>} footer={<><Button variant="ghost">Cancel</Button><Button>Confirm</Button></>}><Text variant="body-sm" tone="muted">Review the details before continuing.</Text></Modal>
                <Drawer title="Filters" description="Side drawer pattern." trigger={<Button variant="outline" leftIcon={<Funnel size={16} />}>Open drawer</Button>}><Text variant="body-sm" tone="muted">Drawer content.</Text></Drawer>
                <Dropdown trigger={<IconButton aria-label="Open menu" icon={<DotsThree size={22} weight="bold" />} variant="outline" />} items={[{ label: "Edit" }, { label: "Duplicate" }, { label: "Delete", destructive: true }]} />
                <Tooltip content="Tooltip names icon-only actions."><IconButton aria-label="Information" icon={<Info size={18} />} variant="outline" /></Tooltip>
                <Tooltip content="White tooltip surface." variant="white"><Button variant="outline">White tooltip</Button></Tooltip>
                <Popover trigger={<Button variant="secondary">Open popover</Button>}><Text variant="title">Popover content</Text><Text variant="body-sm" tone="muted">Lightweight contextual content.</Text></Popover>
              </div>
            )
          }
        ]}
      />
    )
  },
  {
    id: "data-display",
    title: "Data display",
    category: "Data display",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "KPI cards",
              description: "Compact label + value cards for pages that surface KPIs. Use the accent prop to differentiate metrics.",
              code: `<KpiCard label="Total orders" value="05" />
<KpiCard label="Delivered" value="128" accent="success" />
<KpiCard label="NDR pending" value="12" accent="warning" />
<KpiCard label="RTO" value="03" accent="danger" />`,
              children: <DemoKpiCard />
            },
            {
              title: "DataTable, pinning, sorting, visibility",
              code: `<DataTable
  columns={columns}
  data={rows}
  sorting={sorting}
  onSortingChange={setSorting}
  visibleColumnIds={visibleColumnIds}
  leadingColumn={customerColumn}
  selection
  selectedRowIds={selectedIds}
  onSelectedRowIdsChange={setSelectedIds}
  onRowClick={(row) => openCustomer(row.id)}
/>
<TableColumnVisibility columns={columns} visibleColumnIds={visibleColumnIds} onVisibleColumnIdsChange={setVisibleColumnIds} />
<TablePagination page={2} totalPages={6} onPageChange={setPage} />
<Avatar name="Deeksha Gahlot" status="online" />`,
              children: (
                <Stack gap={4}>
                  <DemoDataTable />
                  <DemoPaginationVariants />
                  <div className="bfrs-flex bfrs-gap-3"><Avatar name="Deeksha Gahlot" size="sm" status="online" /><Avatar name="Nykaa Fashion" size="md" status="busy" /><Avatar name="Shiprocket" size="lg" status="offline" /></div>
                </Stack>
              )
            },
            {
              title: "Saved views selector (localStorage + API)",
              description: "Use one free-form screenName for TableViewSelector and TableSaveView. Pass saveApi to the components when they should own backend list/get/update/delete/save while localStorage remains the mirror/fallback.",
              code: `const screenName = "orders_all";
const saveApi = useMemo(() => ({ baseUrl: import.meta.env.VITE_PLATFORM_BASE_URL, viewType: "orders", tab: "all" }), []);
const { views } = useSavedViews(screenName);

<ListPageLayout
  title="Orders"
  filters={
    <>
      <TableViewSelector
        screenName={screenName}
        saveApi={saveApi}
        size="sm"
        onValueChange={(_, view) => {
          if (!view) return;
          setVisibleColumnIds(view.columns.filter((c) => c.visible).map((c) => c.key));
          setFilters(view.filters ?? []);
        }}
      />
      <Select size="sm" value={status} onValueChange={setStatus} options={statusOptions} />
      <TableSaveView
        screenName={screenName}
        saveApi={saveApi}
        viewType="orders"
        columns={visibilityColumns}
        visibleColumnIds={visibleColumnIds}
        filters={filters}
        filtersCount={filters.length}
        onSaved={({ view }) => setActiveViewId(view.view_id)}
      />
    </>
  }
  table={<DataTable data={rows} columns={columns} visibleColumnIds={visibleColumnIds} />}
/>`,
              children: <DemoSavedViewsSelector />
            },
            {
              title: "Standalone table (no toolbar)",
              description: "With no TableToolbar stacked on top, the table rounds its own header — all four corners use the 8px card radius.",
              code: `<DataTable
  columns={columns}
  data={rows}
/>`,
              children: <DemoStandaloneDataTable />
            },
            {
              title: "Empty state (no data)",
              description: "When data is [], the built-in empty view is a two-line message and ships with a default (\"No records found\" + \"There's nothing to show here yet.\"). Override emptyTitle / emptyDescription for your own copy, or pass a fully custom emptyState node.",
              code: `// Default two-line empty message
<DataTable columns={columns} data={[]} />

// Custom copy — override either line
<DataTable
  columns={columns}
  data={[]}
  emptyTitle="No orders yet"
  emptyDescription="New orders will show up here as they come in."
/>`,
              children: <DemoEmptyDataTable />
            },
            {
              title: "Fixed height — sticky header & footer, scrollable rows",
              description: "Set maxHeight to cap the scroll region: the header stays pinned at the top, indexed pagination stays fixed as a footer, and only the rows scroll. The footer's 'Items per page' label and item suffix are customizable text.",
              code: `<DataTable
  columns={columns}
  data={rows}
  selection
  maxHeight={300}
  pagination={{
    page, totalPages: 10,
    variant: "indexed",
    pageSize, pageSizeOptions: [10, 15, 20, 25, 50],
    onPageChange: setPage, onPageSizeChange: setPageSize,
    pageSizeControl: "menu",
    itemsPerPageLabel: "Items per page:", // customizable
    itemLabel: "Orders"                   // customizable
  }}
/>`,
              children: <DemoFixedHeightDataTable />
            },
            {
              title: "Fixed height with column visibility & save",
              description: "All columns enabled with horizontal scroll, column pinning, and a sticky final actions column. Required columns can't be hidden; Show all / Reset live in the Columns menu; Save opens the shared Save this view modal; simple Prev/Next pagination in the footer.",
              code: `const [savedIds, setSavedIds] = useState(allIds);
const [visibleIds, setVisibleIds] = useState(allIds);
const dirty = !sameSet(visibleIds, savedIds);

<TableToolbar
  title="Orders"
  actions={
    <div className="flex items-center gap-2">
      {dirty && (
        <>
          <Button variant="secondary" size="sm" iconOnly aria-label="Discard changes"
            onClick={() => setVisibleIds(savedIds)}><ArrowsClockwise /></Button>
          <TableSaveView
            columns={visibilityColumns}
            visibleColumnIds={visibleIds}
            onSave={(payload) => setSavedIds(payload.columns.filter((c) => c.visible).map((c) => c.key))}
          />
        </>
      )}
      <TableColumnVisibility
        columns={columns.map((c) => ({ id: c.id, label: c.header, disabled: required.includes(c.id) }))}
        visibleColumnIds={visibleIds}
        onVisibleColumnIdsChange={setVisibleIds}
        onReset={() => setVisibleIds(savedIds)}
      />
    </div>
  }
/>
<DataTable columns={columns} data={rows} visibleColumnIds={visibleIds}
  rowActions={() => <IconButton aria-label="Row actions" icon={<DotsThree />} variant="secondary" size="sm" />}
  rowActionsHeader="Actions"
  rowActionsWidth={88}
  stickyRowActions
  maxHeight={300} pagination={{ page, totalPages: 10, variant: "simple", /* … */ }} className="rounded-t-none" />`,
              children: <DemoFixedHeightColumnsDataTable />
            }
          ]}
        />
        <ApiNotes rows={dataTableApiRows} />
        <ComponentPage
          examples={[
            {
              title: "Primitive table — default density with sticky header and footer",
              description: "Table, Thead, Tbody, Tfoot, Tr, Th, Td render a semantic table with DataTable-matching styles. Tr has built-in hover and optional onClick.",
              code: `<Table density="default" aria-label="Orders">
  <Thead sticky>
    <Tr><Th>Order ID</Th><Th>Customer</Th><Th align="right">Amount</Th><Th>Status</Th></Tr>
  </Thead>
  <Tbody>
    {rows.map(row => (
      <Tr key={row.id} onClick={() => setSelected(row.id)}>
        <Td>{row.id}</Td><Td>{row.customer}</Td>
        <Td align="right">{row.amount}</Td><Td>{row.status}</Td>
      </Tr>
    ))}
  </Tbody>
  <Tfoot>
    <Tr><Th colSpan={2}>Total</Th><Th align="right">₹4,70,450</Th><Th /></Tr>
  </Tfoot>
</Table>`,
              children: <DemoPrimitiveTable />
            },
            {
              title: "Compact density with max-height scroll",
              description: "Pass density=\"compact\" to Table — it propagates to all Th and Td via React context. maxHeight enables vertical scroll with the sticky header pinned.",
              code: `<Table density="compact" maxHeight={200}>
  <Thead sticky>
    <Tr><Th>Order ID</Th><Th>Customer</Th><Th align="right">Amount</Th></Tr>
  </Thead>
  <Tbody>
    {rows.map(row => (
      <Tr key={row.id}><Td>{row.id}</Td><Td>{row.customer}</Td><Td align="right">{row.amount}</Td></Tr>
    ))}
  </Tbody>
</Table>`,
              children: <DemoCompactTable />
            }
          ]}
        />
      </Stack>
    )
  },
  {
    id: "navigation",
    title: "Navigation",
    category: "Navigation",
    content: (
      <ComponentPage
        examples={[
          {
            title: "Tabs and breadcrumbs",
            code: `<Breadcrumbs items={[...]} />
<Tabs defaultValue="overview" items={[...]} />`,
            children: (
              <Stack gap={4}>
                <Breadcrumbs items={[{ label: "Home", href: "#" }, { label: "Onboarding", href: "#" }, { label: "Summary" }]} />
                <Tabs defaultValue="overview" items={[{ value: "overview", label: "Overview", content: <Text variant="body-sm">Overview content</Text> }, { value: "settings", label: "Settings", content: <Text variant="body-sm">Settings content</Text> }]} />
              </Stack>
            )
          }
        ]}
      />
    )
  },
  {
    id: "product-patterns",
    title: "Product patterns",
    category: "Patterns",
    content: (
      <Stack gap={5}>
        <ComponentPage
          examples={[
            {
              title: "Page header and layout shell",
              code: `<PageHeader title="Your details are ready" actions={<Button>Go to Dashboard</Button>} />
<LayoutShell header={...} sidebar={...}>...</LayoutShell>`,
              children: (
                <Stack gap={4}>
                  <Paper><Text variant="body-sm" tone="muted">Use PageHeader for title, description, back action, and primary/secondary actions.</Text></Paper>
                  <LayoutShell header={<div className="bfrs-flex bfrs-h-14 bfrs-items-center bfrs-px-5 bfrs-font-semibold"><RocketLaunch className="bfrs-mr-2 bfrs-text-primary" /> Shiprocket UI</div>} sidebar={<Paper><Text variant="body-sm">Sidebar</Text></Paper>} className="bfrs-min-h-[320px] bfrs-rounded-xl bfrs-border bfrs-border-border"><Paper><Text variant="title">Shell content</Text></Paper></LayoutShell>
                </Stack>
              )
            },
            {
              title: "List page layout",
              description: "Full list-page scaffold — pass title, actions, KPI cards, tabs, banners, filters, and the data table as slots; the layout owns the Shiprocket 4.0 spacing and title typography. Every slot except title is optional. Three independent banner positions: above the tabs, under the tabs, and under the filter divider.",
              code: `<ListPageLayout
  title="Orders"
  actions={<Button size="sm">Add Order</Button>}
  kpis={<><KpiCard label="Total orders" value="140" /><KpiCard label="Delivered" value="128" accent="success" /></>}
  bannerAboveTabs={<Alert tone="warning" title="KYC pending">Upload your GST certificate by Friday.</Alert>}
  tabs={<Tabs value={tab} onValueChange={setTab} items={tabItems} />}
  banner={<Alert tone="info" title="Diwali surge">Carrier pickups may be delayed.</Alert>}
  filters={<Select size="sm" value={status} onValueChange={setStatus} options={statusOptions} />}
  bannerBelowFilters={<Tip>Showing orders from the last 30 days.</Tip>}
  table={<DataTable data={orders} columns={columns} />}
/>`,
              children: <DemoListPageLayout />
            },
            {
              title: "Workspace header",
              code: `<WorkspaceHeader
  brand={<ShiprocketBrand />}
  onToggleSidebar={toggleSidebar}
  tabs={<ActiveWorkspaceTab />}
  search={<Input size="sm" placeholder="Search…" leftIcon={<Icon icon={MagnifyingGlass} />} />}
  actions={<Avatar name="Deeksha Gahlot" size="sm" />}
/>

<bfrs-workspace-header toggle [props]="{ search: searchEl, actions: actionsEl }" (toggle-sidebar)="collapseSidebar()">
  <span slot="brand">Shiprocket</span>
</bfrs-workspace-header>`,
              children: <DemoWorkspaceHeader />
            },
            {
              title: "Chat",
              code: `<ChatBubble sender="assistant" eyebrow="Identity" title="Your details are ready">
  We've found your name and email linked to your number.
  <br />
  Review and continue, or edit if needed.
</ChatBubble>
<ChatComposer value={message} onValueChange={setMessage} />
<ChatComposer size="sm" value={message} onValueChange={setMessage} />

<bfrs-chat-composer size="sm" placeholder="Compact composer" (value-change)="message = $event.detail.value"></bfrs-chat-composer>`,
              children: (
                <Stack gap={3}>
                  <ChatBubble sender="assistant" eyebrow="Identity" title="Your details are ready" timestamp="10:30 AM">
                    We've found your name and email linked to your number.
                    <br />
                    Review and continue, or edit if needed.
                  </ChatBubble>
                  <ChatBubble sender="user" timestamp="10:31 AM">Continue with this profile.</ChatBubble>
                  <ChatComposer value="" onValueChange={() => undefined} placeholder="Ask me anything about Shiprocket" />
                  <ChatComposer size="sm" value="" onValueChange={() => undefined} placeholder="Compact composer" />
                </Stack>
              )
            },
            {
              title: "Full-page loader",
              code: `<FullPageLoader />
<FullPageLoader fullScreen={false} titleHighlight="workspace" />`,
              children: <FullPageLoader fullScreen={false} />
            },
            {
              title: "Reveal and copy",
              code: `<RevealAndCopy value={customer.phone} allowCopy />

<RevealAndCopy
  value={customer.email}
  allowCopy={false}
  revealLabel="Show email"
/>`,
              children: <DemoRevealAndCopy />
            },
            {
              title: "Business info display card",
              code: `<BusinessInfoDisplayCard
  businessName="Deeksha Gahlot"
  subtitle="deekshagahlot@gmail.com"
  initials="DG"
  sections={sections}
  className="bfrs-max-w-md"
/>

<BusinessInfoDisplayCard businessName="" sections={[]} className="bfrs-max-w-md" />

<BusinessInfoDisplayCard
  businessName="Deedoodle"
  sections={[{
    title: "Account Details",
    items: [
      { label: "Phone", value: "9876543210" },
      { label: "GST Number", value: undefined },
    ]
  }]}
  className="bfrs-max-w-md"
/>

<BusinessInfoDisplayCardSkeleton />`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 lg:bfrs-grid-cols-2">
                  <BusinessInfoDisplayCard
                    businessName="Deeksha Gahlot"
                    subtitle="deekshagahlot@gmail.com"
                    initials="DG"
                    sections={businessSections}
                    className="bfrs-max-w-md"
                  />
                  <BusinessInfoDisplayCard businessName="" sections={[]} className="bfrs-max-w-md" />
                  <BusinessInfoDisplayCard
                    businessName="Deedoodle"
                    sections={progressiveBusinessSections}
                    className="bfrs-max-w-md"
                  />
                  <BusinessInfoDisplayCardSkeleton />
                </div>
              )
            },
            {
              title: "Step progress card",
              code: `<StepProgressCard
  eyebrow="Identity & Contact"
  title="Getting your details ready…"
  currentStep={1}
  steps={[
    { label: "Securely Fetching to your account" },
    { label: "Verifying your identity" },
    { label: "Retrieving business details" },
    { label: "Validating your information" }
  ]}
/>
<StepProgressCardSkeleton />`,
              children: (
                <div className="bfrs-grid bfrs-gap-5 lg:bfrs-grid-cols-2">
                  <StepProgressCard currentStep={1} steps={progressSteps} />
                  <StepProgressCardSkeleton />
                </div>
              )
            },
            {
              title: "Horizontal stepper",
              code: `<HorizontalStepper
  announcement="Great Choice! Early COD Remittance activation in progress.."
  closeButton
  onClose={handleClose}
  currentStep={0}
  title="Select a Plan"
  subheading="Get guaranteed remittance from the shipment delivered date."
  size="sm"
  variant="primary"
  steps={[
    { label: "Select Plan", secondaryText: "Choose payout speed" },
    { label: "Verify OTP", secondaryText: "Confirm activation" }
  ]}
/>`,
              children: (
                <HorizontalStepper
                  announcement="Great Choice! Early COD Remittance activation in progress.."
                  closeButton
                  onClose={() => undefined}
                  currentStep={0}
                  title="Select a Plan"
                  subheading="Get guaranteed remittance from the shipment delivered date."
                  size="sm"
                  steps={remittanceSteps}
                  variant="primary"
                />
              )
            },
            {
              title: "Display and summary patterns",
              code: `<EntityDisplayCard
  title="Dee-doodles warehouse"
  description="Plot 42, Sector 18, Gurugram, Haryana"
  status="Verified"
  actions={[{ id: "edit", label: "Edit" }]}
  onAction={handleAction}
/>

<SummaryBar
  items={[
    { label: "Subtotal", value: "₹840" },
    { label: "Charges", value: "₹60" }
  ]}
  totalLabel="Total"
  totalValue="₹900"
/>

<Collapsible title="Courier rules">Rules and restrictions</Collapsible>

<Accordion
  type="single"
  defaultValue="pickup"
  items={[
    { value: "pickup", title: "Pickup details", content: "Plot 42, Sector 18, Gurugram." },
    { value: "shipping", title: "Shipping preferences", content: "Standard delivery." }
  ]}
  onValueChange={setOpenSection}
/>

<bfrs-entity-display-card title="Dee-doodles warehouse" status="Verified" [props]="{ actions: entityActions }"></bfrs-entity-display-card>
<bfrs-summary-bar total-label="Total" total-value="₹900" [props]="{ items: summaryItems }"></bfrs-summary-bar>
<bfrs-collapsible title="Courier rules">Rules and restrictions</bfrs-collapsible>
<bfrs-accordion type="single" [props]="{ items: sections }" (value-change)="openSection = $event.detail.value"></bfrs-accordion>`,
              children: <DemoDisplayPatterns />
            }
          ]}
        />
        <ApiNotes
          rows={[
            { name: "className", description: "All pattern components expose className for layout width, spacing, and instance-specific visual overrides." },
            { name: "subtitle", description: "Optional centered identity text, commonly used for email under the profile name." },
            { name: "businessName / sections", description: "Pass businessName=\"\" with sections={[]} for the built-in bootstrap skeleton: static header placeholders, order/category chip placeholders, and six detail rows." },
            { name: "item.value", description: "Pass value: undefined for progressive reveal rows; missing rows render skeletons for both key and value, while rows with values show the real label/value." },
            { name: "eyebrow / title", description: "ChatBubble assistant messages use these optional props for the Figma-derived identity block while keeping children as body copy." },
            { name: "background / gradient", description: "BusinessInfoDisplayCard accepts custom solid backgrounds and CSS gradients while defaulting to the Figma-derived tokenized surface." },
            { name: "headerBackground / headerGradient", description: "Use these props to brand the business-card header while preserving the same card structure." },
            { name: "onValueChange / onSubmit", description: "ChatComposer exposes controlled text updates and submit handling without owning product API logic." },
            { name: "value / allowCopy", description: "RevealAndCopy keeps sensitive values masked until the user reveals them, then optionally exposes a copy action." },
            { name: "currentStep / steps.status", description: "StepProgressCard animates pending, active, done, and disabled transitions from controlled step data." },
            { name: "HorizontalStepper", description: "Use for compact horizontal flows with an optional announcement banner, parent-owned close action, and step-select callbacks." },
            { name: "HorizontalStepper variants", description: "Use primary for theme-colored activation, success for completed/positive flows, info for setup guidance, warning for pending verification, and neutral for low-emphasis wizard steps." },
            { name: "surface", description: "StepProgressCard defaults to plain and supports surface=\"card\"; HorizontalStepper defaults to card and supports surface=\"plain\" for embedding." }
          ]}
        />
      </Stack>
    )
  }
];

function resolveFrameworkPage(page: DocsPage, framework: DocsFramework): DocsPage {
  if (framework !== "angular") {
    return page;
  }

  if (page.id === "installation") {
    return createAngularInstallationPage();
  }

  if (page.id === "usage") {
    return createAngularUsagePage();
  }

  if (page.id === "customization") {
    return createAngularCustomizationPage();
  }

  if (page.id === "layout-primitives") {
    return createAngularLayoutPage();
  }

  if (page.id === "buttons") {
    return createAngularButtonsPage();
  }

  if (page.id === "forms") {
    return createAngularFormsPage();
  }

  if (page.id === "feedback") {
    return createAngularFeedbackPage();
  }

  if (page.id === "overlays") {
    return createAngularOverlaysPage();
  }

  if (page.id === "data-display") {
    return createAngularDataDisplayPage();
  }

  if (page.id === "navigation") {
    return createAngularNavigationPage();
  }

  if (page.id === "product-patterns") {
    return createAngularProductPatternsPage();
  }

  return page;
}

export function getDocsPages(framework: DocsFramework = "react"): DocsPage[] {
  return baseDocsPages.map((page) => resolveFrameworkPage(page, framework));
}

export function getDocsCategories(framework: DocsFramework = "react"): string[] {
  return Array.from(new Set(getDocsPages(framework).map((page) => page.category)));
}

export const docsPages = getDocsPages("react");
export const docsCategories = getDocsCategories("react");
